import { LitElement, html, css, type PropertyValues, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import type {
  ServerMsg, PublicState, LobbyUser, Level, PregamePlayer, HistoryEntry, HistoryUpdate, Moved, Pacing,
  WatchSpeed, Speed, Spectator, TurnStep, Undoable, ReplayFrame, PublicCard, Option, Mulligans, ClockSetting, ClockLength,
} from "./protocol";
import "./spotlight-tour";
import type { SpotlightStep } from "./spotlight-tour";
import { tutorials, tutorialById, tutorialStep, tourSteps, readLearning, saveLearning, learningStyles, type Tutorial } from "./tutorials";
import { Wire, SignedOut, authorizedRequest, readUrlParams, type Credential, type WireStatus } from "./wire";
import { CALLBACK_PATH, freshCredential, signIn, signOut, startAuthKit, type AuthKit, type Session } from "./sign-in";
import { cursors, palette } from "./theme";
import { installClickFeedback } from "./click-feedback";
import { recordPageHit } from "./page-hits";
import { loadSoundEffects, playSound, saveSoundEffects, soundOfMoves } from "./sounds";
import { artUrl } from "./art-url";
import "./card-art";
import "./board/arena-board";
import { renderGameOver } from "./game-over";
import { replayPosition } from "./match-review";
import "./deck-editor";
import "./admin-page";
import { homeDeckOf, renderHero, renderTiles, type HomeDeck, type HomeView, type ListedHomeDeck } from "./home";
import { bannerCardOf, renderGameHistory, renderRecord, type GameHistory, type HistoryPage } from "./profile-history";
import { pageAt, renderTopNav, topNavLinks, topNavStyles, type TopNavTab } from "./top-nav";
import { REPORT_ISSUE, type ReportIssueDetail } from "./report-issue";
import { playerGlowAt, playerPortraitAt } from "./player-avatar";
import { progressOf, progressStyles, renderLevel, renderLevelRoad, renderXpEarned, type Cosmetic, type Progress } from "./progress";
import { seatLooksOf, type CardBack, type SeatLooks, type TableCosmeticsJson } from "./cosmetics";
import type { ClockReading } from "./board/clock";
import type { Looks } from "./board/looks";
import type { BoardIntents, BoardView, PreviewAt } from "./board/arena-board";
import { autoPayAnswer, optionValue, sameStep, stepOf } from "./board/game-text";
import { PacedUpdates, beatMs, isAction, startsTurn, stepBeatMs, stepsPassed, turnBeatMs, type ShownUpdate } from "./board/paced-updates";

type ConnState =
  | "loading-auth" | "signed-out" | "signing-in" | "returning" | "browsing-rooms"
  | "connecting" | "open" | "reconnecting" | "replaced" | "banned" | "gave-up"
  // A finished game whose room has forgotten it, shown from the log R2 keeps (games/<id>/review).
  | "archived";

// The release Worker names the WorkOS client to sign in with (`client_id`, "" for none).
type MeUnauthenticated = { authenticated: false; mode: "workos" | "dev"; client_id: string };
type MeAuthenticated   = { authenticated: true;  user_id: string; display_name: string; mode: "workos" | "dev"; is_admin?: boolean };
type MeResponse = MeAuthenticated | MeUnauthenticated;

type RoomSummary = {
  game_id: string;
  joined: number;
  max_players: number;
  started: boolean;
  game_type: "Standard" | "Commander" | string;
  host: string;
  seated: boolean;
};

type CurrentRoom = { game_id: string; release: number; started: boolean };

// A deck the player may pick, from /decks: what its format finds wrong with it, and the cards this
// release can't play.
type DeckChoice = { deck_id: string; name: string; format: "Standard" | "Commander"; problems: string[]; unplayable: string[] };

const ROOM_SIZES = [2, 3, 4, 5, 6];
// An exhibition seats 2 to 4 AI seats (server/session).
const EXHIBITION_SIZES = [2, 3, 4];
const MATCH_INTRO_MS = 2400;

const SPEEDS: { kind: Speed["kind"]; label: string }[] = [
  { kind: "Half", label: "0.5×" }, { kind: "Normal", label: "1×" }, { kind: "Double", label: "2×" },
  { kind: "Quadruple", label: "4×" }, { kind: "Instant", label: "Instant" },
];

const AI_LEVELS: Level["kind"][] = ["Easy", "Medium", "Hard"];

const GAME_TYPES: { kind: "Standard" | "Commander"; life: number }[] = [{ kind: "Standard", life: 20 }, { kind: "Commander", life: 40 }];

const MULLIGAN_RULES: { kind: Mulligans["kind"]; label: string; title: string }[] = [
  { kind: "NoFreeMulligan", label: "None free", title: "Every mulligan puts a card on the bottom" },
  { kind: "FirstMulliganFree", label: "First free", title: "The first mulligan puts no card on the bottom; later ones do" },
  { kind: "AllMulligansFree", label: "All free", title: "No mulligan puts a card on the bottom" },
];

// The format's own clock (server/clock), then the Host's choices of minutes, or none.
const FORMAT_CLOCK_MINUTES = { Standard: 15, Commander: 35 };
const CLOCK_LENGTHS: ClockLength[] = [
  { kind: "FormatClock", values: [] },
  ...[5, 10, 25, 60].map((minutes): ClockLength => ({ kind: "ClockMinutes", values: [minutes] })),
  { kind: "NoClock", values: [] },
];
const CLOCK_INCREMENTS_SECONDS = [0, 10, 30];

function clockLengthsMatch(a: ClockLength, b: ClockLength): boolean {
  if (a.kind === "ClockMinutes" && b.kind === "ClockMinutes") return a.values[0] === b.values[0];
  return a.kind === b.kind;
}

// Held for Ctrl's hold-priority and the like, never a press of their own.
const MODIFIER_KEYS = ["Control", "Meta", "Shift", "Alt"];

// Above the element, which the page upgrades as soon as it's defined: its settings load then.
const AUTO_PAY_KEY = "spell-mtg.auto-pay";

// On unless this browser turned it off.
function loadAutoPay(): boolean {
  try { return localStorage.getItem(AUTO_PAY_KEY) !== "off"; } catch { return true; }
}

const PREVIEW_AT_KEY = "spell-mtg.preview-at";

const SHOW_LOOKS_KEY = "spell-mtg.show-looks";

// On unless this browser turned it off.
function loadShowLooks(): boolean {
  try { return localStorage.getItem(SHOW_LOOKS_KEY) !== "off"; } catch { return true; }
}

const PREVIEW_PLACES: { at: PreviewAt; label: string }[] = [
  { at: "right", label: "Right" }, { at: "left", label: "Left" }, { at: "middle", label: "Middle" }, { at: "auto", label: "Away from the pointer" },
];

const CONNECTION_LABEL: Partial<Record<ConnState, string>> = {
  connecting: "Connecting", open: "Connected", reconnecting: "Reconnecting", replaced: "Opened in another tab", "gave-up": "Gave up reconnecting",
  archived: "From the saved game log",
};

// On the right unless this browser chose elsewhere.
function loadPreviewAt(): PreviewAt {
  try {
    const saved = localStorage.getItem(PREVIEW_AT_KEY);
    return PREVIEW_PLACES.find((p) => p.at === saved)?.at ?? "right";
  } catch { return "right"; }
}

const LOCAL_HOSTS = ["localhost", "127.0.0.1", "[::1]"];

@customElement("spell-mtg")
class SpellMtg extends LitElement {
  @state() user = "";
  // What opponents see of this player.
  @state() displayName = "";
  // In the Worker's ADMIN_USER_IDS, so the Admin tab shows.
  @state() isAdmin = false;
  @state() authMode: "workos" | "dev" | "unknown" = "unknown";
  @state() authkit: AuthKit | null = null;
  // Why the last sign-in didn't take, shown beside "Sign in".
  @state() signInError: string | null = null;
  @state() editingName = false;
  @state() nameError: string | null = null;
  @state() game = "";
  @state() connState: ConnState = "loading-auth";
  @state() lobbyMessage = "Checking sign-in…";
  @state() seat: string | null = null;
  @state() gameState: PublicState | null = null;
  // `winner` is null for a draw.
  @state() ended = false;
  @state() winner: string | null = null;
  @state() replayFrames: ReplayFrame[] = [];
  private replayCards: PublicCard[] = [];
  @state() replayTotal = 0;
  @state() replayLoading = false;
  @state() replayReceived = 0;
  @state() replaySize = 0;
  @state() replayError: string | null = null;
  @state() lastError: string | null = null;
  @state() startupError: string | null = null;
  @state() availableDecks: DeckChoice[] | null = null;
  // What a Casual room's deck rules found in the deck just picked.
  @state() deckWarnings: string[] = [];
  @state() lobby: LobbyUser[] = [];
  @state() strict = false;
  // The Host's choices for the next AI seat: a deck id from /decks, and how hard it plays.
  @state() aiDeck = "";
  @state() aiLevel: Level["kind"] = "Medium";
  @state() aiSlotOpen: number | null = null;
  @state() gameType: "Standard" | "Commander" = "Standard";
  @state() maxPlayers = 2;
  @state() pregame: PregamePlayer[] = [];
  @state() matchIntro = false;
  @state() rooms: RoomSummary[] = [];
  @state() currentRoom: CurrentRoom | null = null;
  @state() online: number | null = null;
  @state() leavingCurrentRoom = false;
  @state() creatingRoom = false;
  // The History up to the update the board shows.
  @state() history: HistoryEntry[] = [];
  // The cards the shown StateUpdate saw change zones, which the board flies in.
  @state() moves: Moved[] = [];
  // The board shows an update older than one waiting behind it.
  @state() catchingUp = false;
  // A step the Room passed through that the board is walking through, if any.
  @state() walking: TurnStep | null = null;
  // Auto-pay answers a mana payment with the engine's suggestion, convoke's and improvise's tapping
  // too (autoPayAnswer); off, the player picks.
  @state() autoPay = loadAutoPay();
  // Where the board shows the hovered card.
  @state() previewAt: PreviewAt = loadPreviewAt();
  // Off, the table draws every seat plain: no card backs, frames, name plates or battlefield.
  @state() showLooks = loadShowLooks();
  @state() soundEffects = loadSoundEffects();
  @state() roundTripMs: number | null = null;
  @state() bugDetailsCopied = false;
  @state() bugReportOpen = false;
  @state() bugReportSending = false;
  @state() bugReportError: string | null = null;
  @state() bugReportSent = false;
  @state() bugReportTitle = "";
  @state() bugReportHappened = "";
  @state() bugReportExpected = "";
  @state() bugReportFacts: [string, string][] = [];
  // While an auto-pay answer is on its way, its question isn't shown; a refusal shows it.
  @state() autoPaying = false;
  // This player's stops and passing, as the Room last sent them.
  @state() pacing: Pacing | null = null;
  // What this player may undo, as the Room last sent it.
  @state() undoable: Undoable | null = null;
  // Every player's chess clock and the pregame's window, as the Room last sent them.
  @state() clocks: ClockReading | null = null;
  @state() private looks: Looks = {};
  // This page's player sits nowhere and only watches (the room's WatchAccepted).
  @state() spectating = false;
  // Only AI seats play, and the Host watches.
  @state() exhibition = false;
  @state() mulligans: Mulligans["kind"] = "FirstMulliganFree";
  @state() clockSetting: ClockSetting = { length: { kind: "FormatClock", values: [] }, increment_seconds: 0 };
  @state() spectators: Spectator[] = [];
  // The .scenario board the room starts from instead of the pregame, and the
  // Host's draft of one.
  @state() scenario: string | null = null;
  @state() scenarioDraft = "";
  @state() private tourIndex = -1;
  @state() private learning = { tourSeen: false, completed: [] as string[] };
  @state() private coachHidden = false;
  private tutorialSetupPending = false;
  @state() private tutorial = tutorialById(new URL(location.href).searchParams.get("tutorial"));
  // The URL's ?scenario= was sent, or failed to load, so it isn't sent again.
  private urlScenarioTried = false;
  // How fast AI seats answer while someone watches, and whether this player may change it; null
  // until the room says, in a game with no AI seat.
  @state() watchSpeed: WatchSpeed | null = null;
  @state() mayChangeWatchSpeed = false;
  // The open tab, from the address's path, and the player's decks (null while they load), the first
  // eight of which home shows one at a time from `heroIndex`.
  @state() page: TopNavTab = pageAt(new URL(location.href)) ?? "home";
  @state() homeDecks: HomeDeck[] | null = null;
  @state() heroIndex = 0;
  @state() private lessonsOpen = false;
  // The profile's finished games, a page at a time (null while the first loads).
  @state() gameHistory: HistoryPage | null = null;
  @state() gameHistoryError: string | null = null;
  @state() loadingMoreHistory = false;
  // The player's level, read as they sign in and again after a game ends; null until read.
  @state() progress: Progress | null = null;
  // The progress before and after the game that just ended, once the server has counted it.
  @state() progressEarned: { before: Progress; after: Progress } | null = null;
  // The card back, frame and name plate each seat's player chose, by seat.
  @state() seatLooks: { [seat: string]: SeatLooks } = {};
  private seatLooksAskedFor = "";
  private cardBacks: Promise<CardBack[]> | null = null;

  private wire: Wire | null = null;
  // The latest update the Room has sent, whose History each NewEntries extends.
  private received: ShownUpdate | null = null;
  // Each StateUpdate in turn, an AI seat's action shown a beat before the next.
  private readonly updates = new PacedUpdates((u) => this.showUpdate(u), (u, before) => this.beatAfter(u, before));
  // The update the board showed last, whose turn the next one may start.
  private lastShown: ShownUpdate | null = null;
  // The click that ends a press which showed the latest update instead of acting on the one shown.
  private swallowClick = false;
  // This player asked to leave, and the page goes home once the Room lets them.
  private leaving = false;
  private dropsAt = new Map<string, number>();
  @state() private droppedNow: string[] = [];
  private dropTimer: number | null = null;
  private session: Session | null = null;
  private roomsPollTimer: number | null = null;
  private matchIntroTimer: number | null = null;

  // What the board sends, each as one message to the Room.
  private readonly intents: BoardIntents = {
    takeAction: (action, holdingPriority) => {
      if (holdingPriority) this.wire?.takeActionHoldingPriority(action);
      else this.wire?.takeAction(action);
    },
    endTurn: (turn) => this.wire?.endTurn(turn),
    skipTurn: (turn) => this.wire?.skipTurn(turn),
    resolveStack: () => this.wire?.resolveStack(),
    repeatLoop: (times) => this.wire?.repeatLoop(times),
    setStops: (stops) => {
      if (this.pacing) this.pacing = { ...this.pacing, stops };
      this.wire?.setStops(stops);
    },
    setAutoPassCards: (names) => {
      if (this.pacing) this.pacing = { ...this.pacing, auto_pass_cards: names };
      this.wire?.setAutoPassCards(names);
    },
    setAutoAnswers: (answers) => {
      if (this.pacing) this.pacing = { ...this.pacing, auto_answers: answers };
      this.wire?.setAutoAnswers(answers);
    },
    answerChoice: (answer) => this.wire?.choiceAnswer(answer),
    declareAttackers: (attacks) => this.wire?.declareAttackers(attacks),
    declareBlockers: (blocks) => this.wire?.declareBlockers(blocks),
    setAutoPay: (on) => this.setAutoPay(on),
    undoLastAction: () => this.wire?.undoLastAction(),
    undoToLastPass: () => this.wire?.undoToLastPass(),
    dismissError: () => { this.lastError = null; },
    leaveGame: () => this.leaveRoom(),
    chooseStartingPlayer: (seat) => this.wire?.chooseStartingPlayer(seat),
    keepHand: () => this.wire?.keepHand(),
    mulligan: () => this.wire?.mulligan(),
    bottomCards: (oids) => this.wire?.bottomCards(oids),
    lookAt: (look) => this.wire?.lookAt(look),
  };

  static styles = [palette, cursors, topNavStyles, progressStyles, learningStyles, css`
    :host { display: block; font-family: var(--font-ui); font-size: 14px; line-height: 1.5; color: var(--text); }
    button, select, input { font-family: inherit; }
    button:focus-visible, select:focus-visible, input:focus-visible, a:focus-visible { outline: 2px solid var(--lavender); outline-offset: 2px; }
    input[type="checkbox"], input[type="radio"] { accent-color: var(--brand-hi); width: 15px; height: 15px; }
    a { color: var(--lavender); text-decoration: none; }
    a:hover { text-decoration: underline; }

    .shell { max-width: 1100px; margin: 0 auto; padding: 24px 24px 64px; }
    .link-button { background: none; border: 0; padding: 0; font: inherit; color: var(--lavender); cursor: var(--cursor-pointer, pointer); }
    .link-button:hover { text-decoration: underline; }
    .name-form { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
    .name-form input { font: inherit; padding: 6px 10px; border-radius: 3.5px; border: 1px solid #000; background: var(--input); color: #bbb; margin-left: 6px; }
    .bug-report-dialog {
      box-sizing: border-box; width: min(92vw, 600px); max-height: 92vh; padding: 0; border-radius: 8px; color: var(--text);
      background: var(--surface); border: 1px solid var(--border-strong); box-shadow: 0 20px 60px rgb(0 0 0 / 0.6);
    }
    .bug-report-dialog::backdrop { background: rgb(0 0 0 / 0.6); }
    .bug-report-dialog h3 { margin: 0; font-size: 18px; font-weight: 700; color: var(--text-strong); }
    .bug-report-dialog p { margin: 0; }
    .bug-report-dialog .sheet { display: grid; gap: 12px; padding: 18px 22px 20px; }
    .bug-report-dialog .row-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .bug-report-dialog summary { color: var(--text-dim); }
    .bug-report { display: grid; gap: 12px; width: 100%; }
    .bug-report label { display: grid; gap: 6px; }
    .bug-report input, .bug-report textarea { box-sizing: border-box; width: 100%; padding: 8px; font: inherit; color: var(--text, #ddd); background: var(--input); border: 1px solid #777; border-radius: 4px; }
    .bug-report textarea { resize: vertical; }
    .bug-report pre { white-space: pre-wrap; overflow-wrap: anywhere; font-size: 12px; }
    .name-form .error { flex-basis: 100%; margin: 4px 0 0; }

    /* Moxfield's deck banner: a purple band with a big bold title. */
    .banner {
      display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; flex-wrap: wrap;
      margin: 0 0 26px; padding: 36px 32px 28px; border-radius: 8px;
      background: linear-gradient(100deg, #731fa1 0%, #521578 55%, #2c0c42 100%);
    }
    .banner h1 { margin: 0; font-size: 44px; font-weight: 700; line-height: 1.1; color: var(--text-strong); }
    .banner p { margin: 10px 0 0; display: flex; flex-wrap: wrap; gap: 8px; align-items: center; color: rgb(255 255 255 / 0.82); }
    .banner .tag { font-size: 11px; line-height: 1; padding: 4px 7px; border-radius: 5.25px; background: var(--tag); color: var(--text-strong); }
    .banner button { font: inherit; font-size: 14px; font-weight: 600; padding: 9px 18px; border: 0; border-radius: 5px; cursor: var(--cursor-pointer, pointer); background: var(--text-strong); color: #4b1170; }
    .banner button:hover:not(:disabled) { background: #f1e6f8; }
    .banner button:disabled { opacity: 0.6; cursor: var(--cursor-default, not-allowed); }

    /* ---------- home ---------- */
    .home-top { display: grid; grid-template-columns: minmax(0, 2.2fr) minmax(250px, 1fr); gap: 14px; margin-bottom: 8px; }
    .hero, .tile { position: relative; isolation: isolate; overflow: hidden; border-radius: 8px; background: var(--surface-3); }
    .hero::before, .tile::before { content: ""; position: absolute; z-index: -2; background: var(--art, none) center 22% / cover no-repeat; transition: transform 400ms var(--ease-out); }
    .hero::after, .tile::after { content: ""; position: absolute; inset: 0; z-index: -1; }
    .hero { min-height: 340px; display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; padding: 32px 64px 44px 36px; }
    .hero::before { inset: -40px; filter: blur(14px) saturate(1.15); opacity: 0.75; }
    .hero::after { background: linear-gradient(100deg, rgb(115 31 161 / 0.92) 0%, rgb(82 21 120 / 0.7) 42%, rgb(44 12 66 / 0.15) 100%), linear-gradient(to top, rgb(0 0 0 / 0.35), transparent 50%); }
    .hero.loading::after { background: linear-gradient(100deg, rgb(115 31 161 / 0.5), rgb(44 12 66 / 0.2)); }
    .hero h1 { margin: 0; font-size: 44px; font-weight: 700; line-height: 1.1; color: var(--text-strong); }
    .hero-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 10px 0 18px; color: rgb(255 255 255 / 0.85); }
    .hero-meta .tag { font-size: 11px; line-height: 1; padding: 4px 7px; border-radius: 5.25px; background: var(--tag); color: var(--text-strong); }
    .pips { display: inline-flex; gap: 3px; }
    .pip { width: 18px; height: 18px; border-radius: 50%; display: inline-grid; place-items: center; font-size: 10px; font-weight: 700; color: #222; box-shadow: -1px 1px 0 #111; background: var(--mana-c); }
    .pip.W { background: var(--mana-w); } .pip.U { background: var(--mana-u); } .pip.B { background: var(--mana-b); } .pip.R { background: var(--mana-r); } .pip.G { background: var(--mana-g); }
    .hero-link { display: inline-block; padding: 9px 18px; border-radius: 5px; font-weight: 600; background: var(--text-strong); color: #4b1170; }
    .hero-link:hover { background: #f1e6f8; text-decoration: none; }
    .hero-card { flex: none; align-self: center; --card-w: 168px; --card-h: 235px; transform: rotate(4deg); filter: drop-shadow(0 18px 30px rgb(0 0 0 / 0.6)); }
    .hero-step { position: absolute; top: 50%; transform: translateY(-50%); width: 36px; height: 36px; border-radius: 50%; border: 0; cursor: var(--cursor-pointer, pointer); font: inherit; font-size: 24px; line-height: 1; color: var(--text-strong); background: rgb(0 0 0 / 0.35); }
    .hero-step:hover { background: rgb(0 0 0 / 0.6); }
    .hero-step.prev { left: 10px; }
    .hero-step.next { right: 10px; }
    .hero-dots { position: absolute; left: 50%; bottom: 14px; transform: translateX(-50%); display: flex; gap: 7px; }
    .dot { width: 8px; height: 8px; padding: 0; border: 0; border-radius: 50%; cursor: var(--cursor-pointer, pointer); background: rgb(255 255 255 / 0.35); }
    .dot.on { background: var(--text-strong); }
    .tiles { display: grid; grid-template-rows: repeat(3, minmax(0, 1fr)); gap: 12px; }
    .tile { display: flex; flex-direction: column; justify-content: flex-end; gap: 2px; min-height: 100px; padding: 12px 16px; color: var(--text-strong); }
    .tile::before { inset: 0; background-size: 128% auto; background-position: 50% 21%; }
    .tile::after { background: linear-gradient(to top, rgb(17 17 17 / 0.92) 0%, rgb(17 17 17 / 0.55) 45%, rgb(17 17 17 / 0.1) 100%); }
    .tile:hover { text-decoration: none; }
    .tile:hover::before { transform: scale(1.05); }
    .tile-title { font-size: 16px; font-weight: 700; }
    .tile-sub { font-size: 13px; color: rgb(255 255 255 / 0.78); }
    .rooms h3 { scroll-margin-top: 16px; }
    .play {
      position: fixed; right: 32px; bottom: 32px; z-index: 50; font: inherit; font-size: 24px; font-weight: 700; line-height: 1; padding: 15px 58px 16px;
      border-radius: 32px; cursor: var(--cursor-pointer, pointer); color: var(--text-strong); background: var(--action-orange); border: 1px solid var(--action-orange-edge);
      box-shadow: 0 0 20px 4px var(--action-orange-glow), inset 0 1px 0 var(--action-orange-shine); transition: filter 150ms, transform 120ms;
    }
    .play:hover:not(:disabled) { filter: brightness(1.08); }
    .play:active:not(:disabled) { transform: translateY(1px); }
    .play:disabled { opacity: 0.6; cursor: var(--cursor-default, wait); }
    .profile-banner { justify-content: flex-start; align-items: center; gap: 22px; }
    .profile-banner.with-art { position: relative; isolation: isolate; overflow: hidden; background: var(--surface-3); }
    .profile-banner.with-art::before { content: ""; position: absolute; inset: -24px; z-index: -2; background: var(--art) center 22% / cover no-repeat; filter: blur(6px) saturate(1.1); }
    .profile-banner.with-art::after { content: ""; position: absolute; inset: 0; z-index: -1; background: linear-gradient(100deg, rgb(115 31 161 / 0.9) 0%, rgb(82 21 120 / 0.55) 45%, rgb(20 6 30 / 0.6) 100%), linear-gradient(to top, rgb(0 0 0 / 0.35), transparent 60%); }
    .profile-face { flex: none; width: 84px; height: 84px; border-radius: 50%; display: grid; place-items: center; font-size: 38px; font-weight: 700; color: var(--text-strong); background: rgb(0 0 0 / 0.25); box-shadow: inset 0 0 0 3px rgb(255 255 255 / 0.5); }
    .record { margin-left: auto; display: grid; justify-items: end; gap: 3px; text-align: right; }
    .record-overall { font-size: 44px; font-weight: 700; line-height: 1.1; font-variant-numeric: tabular-nums; color: var(--text-strong); }
    .record-key { font-size: 12px; color: rgb(255 255 255 / 0.6); }
    .record-split { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 4px 16px; margin-top: 8px; font-size: 13px; color: rgb(255 255 255 / 0.75); }
    .record-split b { font-weight: 600; font-variant-numeric: tabular-nums; color: var(--text-strong); margin-left: 3px; }
    .history-table td { vertical-align: middle; }
    .history-table small { display: block; margin-top: 2px; font-size: 12px; color: var(--text-faint); }
    .history-result b { font-weight: 700; }
    .history-game.win .history-result b { color: var(--good); }
    .history-game.loss .history-result b { color: var(--bad); }
    .history-game.draw .history-result b { color: var(--text-strong); }
    .history-game.uncounted .history-result b { color: var(--text-dim); }
    .history-deck { display: flex; align-items: center; gap: 12px; min-width: 0; }
    .history-deck b { color: var(--text-strong); font-weight: 600; }
    .history-art { flex: none; width: 54px; height: 40px; border-radius: 4px; background: var(--art, none) center 22% / 132% auto no-repeat, var(--surface-3); box-shadow: inset 0 0 0 1px rgb(0 0 0 / 0.35); }
    .history-art.no-art { visibility: hidden; }
    .history-opponents { line-height: 1.9; }
    .history-opponents > * { display: inline-block; vertical-align: middle; margin-right: 6px; }
    .history-opponents .person { color: var(--text); }
    .history-table .turns { text-align: right; font-variant-numeric: tabular-nums; }
    .history-ended { color: var(--text-dim); white-space: nowrap; }
    .history-replay { text-align: right; }
    .history-replay a { font-weight: 600; }
    .history-more { margin: 14px 0 0; }
    .lobby .setting { display: flex; align-items: flex-start; gap: 10px; max-width: 560px; }
    .setting input { margin-top: 3px; }
    .setting b { display: block; color: var(--text-strong); font-weight: 600; }
    .setting .hint { display: block; }
    .lobby .setting + .setting { margin-top: 16px; }
    .setting-options { display: flex; flex-wrap: wrap; gap: 6px 18px; margin-top: 8px; }
    .setting-options input { margin: 0; }
    .setting.cosmetic > span { flex: 1; }
    .setting.cosmetic cosmetic-picker { margin-top: 8px; }
    .match-settings { position: relative; display: flex; flex-wrap: wrap; align-items: center; gap: 12px 32px; padding: 14px 0; border-top: 1px solid var(--border); }
    .match-setting { display: flex; align-items: center; gap: 12px; }
    .match-setting-label, .match-settings-note { color: var(--text-dim); font-size: 13px; }
    .match-settings-note { margin-left: auto; color: var(--text-faint); }
    .segmented { display: inline-flex; padding: 3px; gap: 2px; border: 1px solid #000; border-radius: 6px; background: var(--input); }
    .lobby .segmented button { padding: 6px 14px; border-radius: 4px; font-size: 13px; font-weight: 500; background: transparent; color: var(--text-dim); }
    .lobby .segmented button small { margin-left: 4px; color: var(--text-faint); font-size: 12px; font-weight: 400; }
    .lobby .segmented button:hover:not(:disabled) { background: var(--surface-2); color: var(--text); }
    .lobby .segmented button.on { background: var(--surface-3); color: var(--text-strong); font-weight: 600; }
    .lobby .segmented button.on small { color: var(--text-dim); }
    .lobby .segmented button:disabled { cursor: var(--cursor-default, default); }
    .lobby .segmented button:disabled:not(.on) { background: transparent; color: var(--text-faint); }
    .seat-lobby { margin: 24px 0 20px; }
    .seat-lobby-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 12px; }
    .seat-lobby-heading h2 { margin: 0; color: var(--text-strong); font-size: 18px; line-height: 1.3; }
    .seat-lobby-heading p { margin: 2px 0 0; color: var(--text-dim); font-size: 13px; }
    .seat-stepper { display: flex; align-items: center; gap: 8px; padding: 4px; border: 1px solid var(--border); border-radius: 8px; background: var(--surface-2); }
    .lobby .seat-stepper button { width: 32px; height: 32px; padding: 0; border-radius: 6px; font-size: 20px; line-height: 1; }
    .seat-stepper-value { min-width: 56px; text-align: center; color: var(--text-strong); font-weight: 650; font-variant-numeric: tabular-nums; }
    .seat-stepper-value small { display: block; color: var(--text-faint); font-size: 11px; line-height: 1.1; font-weight: 500; }
    .player-slots { display: flex; flex-direction: column; border-top: 1px solid var(--border); }
    .player-slot { display: grid; grid-template-columns: 104px minmax(0, 1fr) minmax(220px, .8fr) 32px; align-items: center; gap: 16px; min-width: 0; min-height: 116px; padding: 12px 8px; border-bottom: 1px solid var(--border); }
    .player-slot-portrait { position: relative; align-self: stretch; margin: -12px 0; }
    .player-slot-portrait img { position: absolute; left: 44%; bottom: 0; display: block; width: auto; max-width: none; height: calc(100% - 6px); transform: translateX(-50%); pointer-events: none; filter: drop-shadow(0 3px 7px #080b10); }
    .player-slot.you .player-slot-portrait img { filter: drop-shadow(0 0 8px rgb(201 153 230 / .38)) drop-shadow(0 3px 7px #080b10); }
    .player-slot-person { position: relative; display: flex; align-items: center; min-width: 0; }
    .player-slot-person-copy { display: flex; flex-direction: column; gap: 5px; min-width: 0; line-height: 1.35; }
    .player-slot-name-line { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; min-width: 0; }
    .player-slot-person-copy b { overflow: hidden; color: var(--text-strong); font-size: 17px; font-weight: 650; text-overflow: ellipsis; white-space: nowrap; }
    .player-slot-person-copy > span { color: var(--text-dim); font-size: 12px; }
    .player-slot-number { color: var(--text-faint); font-size: 12px; }
    .player-slot-details { position: relative; display: flex; flex-direction: column; align-items: flex-start; gap: 6px; min-width: 0; }
    .player-slot-deck-label { color: var(--text-faint); font-size: 12px; }
    .player-slot-deck { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; min-width: 0; }
    .player-slot-deck select { max-width: 100%; min-width: 0; }
    .lobby select.deck-illegal { border-color: var(--bad); box-shadow: inset 0 -2px 0 var(--bad); }
    .lobby option.breaks-rules { color: var(--bad); }
    .lobby .deck-warnings .hint { color: var(--bad); }
    .player-slot-deck .deck-editor-link { font-size: 12px; }
    .player-slot-state { color: var(--text-faint); font-size: 12px; }
    .player-slot-state.ready { color: var(--good); }
    .lobby .slot-remove { display: grid; place-items: center; flex: none; width: 28px; height: 28px; padding: 0; border: 1px solid var(--border); border-radius: 50%; color: var(--text-dim); background: transparent; font-size: 19px; line-height: 1; }
    .lobby .slot-remove:hover:not(:disabled) { color: var(--bad); border-color: var(--bad); background: var(--bad-soft); }
    .player-slot.empty { grid-template-columns: 104px minmax(0, 1fr); color: var(--text-dim); }
    .player-slot.empty.configuring { grid-template-columns: 104px minmax(0, 1fr) minmax(300px, 1.2fr); }
    button.player-slot.empty { width: 100%; text-align: left; cursor: var(--cursor-pointer, pointer); font: inherit; background: transparent; }
    button.player-slot.empty:hover:not(:disabled) { background: rgb(151 93 190 / .055); }
    button.player-slot.empty:disabled { opacity: .52; cursor: var(--cursor-default, not-allowed); }
    .empty-slot-plus { justify-self: center; display: grid; place-items: center; width: 58px; height: 58px; border: 1px dashed #6e6378; border-radius: 50%; color: var(--lavender); font-size: 29px; font-weight: 300; line-height: 1; }
    .empty-slot-plus.waiting { border-color: var(--border); color: var(--text-faint); font-size: 22px; }
    .empty-slot-copy { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
    .player-slot.empty b { color: var(--text-strong); font-size: 15px; font-weight: 600; }
    .player-slot.empty .empty-slot-copy > span:last-child { color: var(--text-faint); font-size: 12px; }
    .slot-ai-form { display: grid; grid-template-columns: minmax(0, 1fr) minmax(100px, .7fr); gap: 7px; width: 100%; }
    .slot-ai-form select { min-width: 0; width: 100%; font-size: 12px; }
    .slot-ai-actions { grid-column: 1 / -1; display: flex; justify-content: flex-start; gap: 7px; }
    .lobby .slot-ai-actions button { padding: 6px 11px; font-size: 12px; }
    .lobby .slot-ai-actions .secondary { background: transparent; }
    .match-intro { position: fixed; inset: 0; z-index: 300; overflow: hidden; color: var(--text-strong); background: #07060b; --seam-top: 57%; --seam-bottom: 45%; }
    .vs-side { position: absolute; inset: 0; background: radial-gradient(ellipse 34% 62% at var(--glow-x) 64%, color-mix(in oklab, var(--glow) 62%, transparent), transparent 72%), radial-gradient(ellipse 70% 90% at var(--glow-x) 100%, color-mix(in oklab, var(--glow) 30%, #07060b), #07060b 78%); }
    .vs-side::after { content: ""; position: absolute; inset: 0; background: linear-gradient(to top, rgb(4 3 8 / .92), transparent 38%); pointer-events: none; }
    .vs-side.left { --glow-x: 24%; clip-path: polygon(0 0, var(--seam-top) 0, var(--seam-bottom) 100%, 0 100%); animation: vs-from-left 420ms cubic-bezier(.16,.84,.3,1) both; }
    .vs-side.right { --glow-x: 77%; clip-path: polygon(var(--seam-top) 0, 100% 0, 100% 100%, var(--seam-bottom) 100%); animation: vs-from-right 420ms cubic-bezier(.16,.84,.3,1) both; }
    .vs-avatar { position: absolute; bottom: 0; left: var(--glow-x); width: auto; height: min(84vh, 880px); max-width: 64vw; object-fit: contain; object-position: bottom; transform: translateX(-50%); -webkit-mask-image: linear-gradient(to top, transparent 4%, #000 34%); mask-image: linear-gradient(to top, transparent 4%, #000 34%); }
    .vs-side.right .vs-avatar { transform: translateX(-50%) scaleX(-1); }
    .vs-plate { position: absolute; z-index: 1; bottom: clamp(36px, 9vh, 88px); max-width: 38vw; }
    .vs-side.left .vs-plate { left: clamp(20px, 5vw, 80px); }
    .vs-side.right .vs-plate { right: clamp(20px, 5vw, 80px); text-align: right; }
    .vs-plate b { display: block; overflow: hidden; padding-bottom: 10px; border-bottom: 3px solid var(--glow); font-size: clamp(28px, 4.4vw, 62px); font-weight: 700; line-height: 1; letter-spacing: -.03em; text-overflow: ellipsis; white-space: nowrap; text-shadow: 0 4px 24px rgb(0 0 0 / .6); }
    .vs-plate span { display: block; overflow: hidden; margin-top: 10px; color: rgb(255 255 255 / .72); font-size: clamp(13px, 1.3vw, 17px); font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
    .vs-seam { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; pointer-events: none; filter: drop-shadow(0 0 6px #fff) drop-shadow(0 0 22px rgb(255 255 255 / .55)); animation: vs-seam-draw 360ms 260ms ease-out both; }
    .vs-seam line { stroke: #fff; stroke-width: 2px; }
    .vs-seam .stacked { display: none; }
    .vs-mark { position: absolute; top: 50%; left: calc((var(--seam-top) + var(--seam-bottom)) / 2); z-index: 2; margin: 0; font-size: clamp(76px, 11vw, 176px); font-weight: 700; line-height: 1; letter-spacing: -.07em; transform: translate(-52%, -54%) skewX(-12deg); background: linear-gradient(176deg, #fff 30%, #d9d3e8 52%, #8a809f 74%, #e8e2f4); -webkit-background-clip: text; background-clip: text; color: transparent; filter: drop-shadow(0 6px 0 rgb(0 0 0 / .55)) drop-shadow(0 0 32px rgb(255 255 255 / .35)); animation: vs-slam 380ms 340ms cubic-bezier(.3,1.4,.5,1) both; }
    .vs-mark::after { content: ""; position: absolute; inset: -40%; border-radius: 50%; background: radial-gradient(circle, rgb(255 255 255 / .75), transparent 62%); animation: vs-flash 520ms 420ms ease-out both; }
    .vs-format { position: absolute; z-index: 2; top: clamp(18px, 4vh, 36px); left: 0; right: 0; margin: 0; color: rgb(255 255 255 / .68); font-size: 13px; font-weight: 600; text-align: center; }
    .vs-status { position: absolute; z-index: 2; bottom: clamp(16px, 3.4vh, 30px); left: 50%; display: grid; justify-items: center; gap: 8px; color: rgb(255 255 255 / .7); font-size: 12px; font-weight: 500; transform: translateX(-50%); }
    .vs-status::after { content: ""; width: 120px; height: 2px; border-radius: 2px; background: linear-gradient(90deg, #fff var(--progress, 0%), rgb(255 255 255 / .18) 0); animation: vs-progress var(--intro-ms) linear both; }
    .vs-table { position: absolute; inset: 0; display: flex; padding-inline: 1.5vw; }
    .vs-table .vs-side { position: relative; inset: auto; flex: 1; min-width: 0; --glow-x: 50%; clip-path: polygon(6vw 0, 100% 0, calc(100% - 6vw) 100%, 0 100%); animation: vs-rise 420ms calc(var(--i) * 90ms) cubic-bezier(.16,.84,.3,1) both; }
    .vs-table .vs-side + .vs-side { margin-left: -5vw; }
    .vs-table .vs-avatar { height: min(76vh, 780px); max-width: 32vw; }
    .vs-table .vs-plate { left: 5vw; right: 7vw; max-width: none; text-align: left; }
    .vs-table .vs-plate b { font-size: clamp(22px, 2.8vw, 40px); }
    .sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
    .vs-empty { position: absolute; inset: 0; display: grid; place-content: center; text-align: center; }
    .vs-empty b { font-size: clamp(26px, 4vw, 44px); font-weight: 700; letter-spacing: -.02em; }
    @property --progress { syntax: "<percentage>"; inherits: false; initial-value: 0%; }
    @keyframes vs-from-left { from { opacity: 0; transform: translateX(-18%); } }
    @keyframes vs-from-right { from { opacity: 0; transform: translateX(18%); } }
    @keyframes vs-rise { from { opacity: 0; transform: translateY(12%); } }
    @keyframes vs-from-below { from { opacity: 0; transform: translateY(14%); } }
    @keyframes vs-from-above { from { opacity: 0; transform: translateY(-14%); } }
    @keyframes vs-seam-draw { from { clip-path: inset(0 0 100% 0); } to { clip-path: inset(0); } }
    @keyframes vs-slam { from { opacity: 0; transform: translate(-52%, -54%) skewX(-12deg) scale(2.6); } }
    @keyframes vs-flash { from { opacity: 1; transform: scale(.4); } to { opacity: 0; transform: scale(1.5); } }
    @keyframes vs-progress { to { --progress: 100%; } }
    @media (prefers-reduced-motion: reduce) {
      .vs-side, .vs-table .vs-side, .vs-mark, .vs-mark::after, .vs-status::after { animation: none; }
      .vs-mark::after { display: none; }
      .vs-seam { animation: none; }
    }
    .advanced-settings { flex: 1 1 100%; margin-top: -12px; }
    .advanced-settings summary {
      position: absolute; top: 14px; right: 0; display: flex; align-items: center; gap: 9px; height: 36px; padding: 0 2px;
      color: var(--text-dim); font-size: 13px; list-style: none; cursor: var(--cursor-pointer, pointer); user-select: none;
    }
    .advanced-settings summary:hover { color: var(--text-strong); }
    .advanced-settings summary::-webkit-details-marker { display: none; }
    .advanced-settings summary:focus-visible { outline: 2px solid var(--lavender); outline-offset: 3px; border-radius: 3px; }
    .advanced-settings summary::after {
      content: ""; flex: none; width: 6px; height: 6px; margin-top: -4px; border-right: 1.5px solid currentColor; border-bottom: 1.5px solid currentColor;
      transform: rotate(45deg); transition: transform 160ms ease;
    }
    .advanced-settings[open] summary::after { transform: rotate(225deg); margin-top: 3px; }
    .advanced-settings-content { padding: 14px 0 0; }
    .scenario-box textarea { flex: 1 1 100%; font: 12px/1.5 ui-monospace, monospace; padding: 8px 10px; border-radius: 3.5px; border: 1px solid #000; background: var(--input); color: var(--text); resize: vertical; }
    .advanced-settings-content .lobby-row:first-child { margin-top: 0; }
    .advanced-settings-content .lobby-row:last-child { margin-bottom: 0; }
    /* Narrow screens: the decks over the shortcuts, and on a phone the deck's name without its card. */
    @media (max-width: 800px) {
      .home-top { grid-template-columns: minmax(0, 1fr); }
      .tiles { grid-template-rows: none; grid-template-columns: repeat(3, minmax(0, 1fr)); }
    }
    @media (max-width: 600px) {
      .shell { padding: 16px 16px 64px; }
      .hero { min-height: 220px; padding: 28px 20px 56px; }
      .hero h1, .banner h1 { font-size: 32px; }
      .hero-card { display: none; }
      .hero-step { top: auto; bottom: 8px; transform: none; width: 32px; height: 32px; font-size: 22px; }
      .tiles { grid-template-columns: minmax(0, 1fr); }
      .tile { min-height: 84px; }
      .banner { padding: 26px 20px 22px; }
      .profile-banner { gap: 16px; }
      .profile-face { width: 64px; height: 64px; font-size: 30px; }
      .record { flex-basis: 100%; margin-left: 0; justify-items: start; text-align: left; }
      .record-overall { font-size: 32px; }
      .record-split { justify-content: flex-start; margin-top: 4px; }
      .history-table :is(th, td):is(:nth-child(4), :nth-child(5)) { display: none; }
      .history-table :is(th, td) { padding-left: 6px; padding-right: 6px; }
      .history-art { display: none; }
      .play { right: 16px; bottom: 16px; font-size: 20px; padding: 13px 44px 14px; }
      .seat-lobby-heading { align-items: flex-start; }
      .player-slot { grid-template-columns: 72px minmax(0, 1fr) 30px; gap: 10px; min-height: 96px; padding: 12px 4px; }
      .player-slot-portrait { grid-row: 1 / span 2; }
      .player-slot-portrait img { left: 40%; max-height: 96px; }
      .advanced-settings { margin-top: 0; }
      .advanced-settings summary { position: static; height: auto; }
      .player-slot-person { grid-column: 2; grid-row: 1; }
      .player-slot-person-copy b { font-size: 15px; }
      .player-slot-details { grid-column: 2 / -1; grid-row: 2; gap: 4px; }
      .player-slot-deck-label { display: none; }
      .player-slot-deck { gap: 6px; }
      .player-slot-deck select { max-width: min(210px, 62vw); }
      .player-slot > .slot-remove { grid-column: 3; grid-row: 1; }
      .player-slot.empty { grid-template-columns: 72px minmax(0, 1fr); }
      .player-slot.empty.configuring { grid-template-columns: 72px minmax(0, 1fr); }
      .player-slot.empty .empty-slot-plus { width: 46px; height: 46px; }
      .player-slot.empty.configuring .slot-ai-form { grid-column: 2; }
      .slot-ai-form { grid-template-columns: minmax(0, 1fr) minmax(90px, .65fr); }
      .vs-side.left { --glow-x: 34%; clip-path: polygon(0 54%, 100% 46%, 100% 100%, 0 100%); animation-name: vs-from-below; }
      .vs-side.right { --glow-x: 66%; clip-path: polygon(0 0, 100% 0, 100% 46%, 0 54%); animation-name: vs-from-above; }
      .vs-side.right::after { background: linear-gradient(to top, transparent 46%, rgb(4 3 8 / .88) 52%, transparent 76%); }
      .vs-avatar { height: 44vh; max-width: 86vw; }
      .vs-side.right .vs-avatar { bottom: 48%; }
      .vs-side.right .vs-plate { bottom: calc(50% + 62px); }
      .vs-plate { max-width: calc(100% - 40px); }
      .vs-plate b { font-size: clamp(26px, 8vw, 40px); }
      .vs-seam .split { display: none; }
      .vs-seam .stacked { display: inline; }
      .vs-mark { left: 50%; font-size: clamp(64px, 20vw, 96px); }
      .vs-format { top: 16px; }
      .vs-table { inset: 0; flex-direction: column; }
      .vs-table { padding: 0; }
      .vs-table .vs-side, .vs-table .vs-side + .vs-side { margin: 0; clip-path: none; --glow-x: 74%; }
      .vs-table .vs-avatar { height: 100%; max-width: 46vw; }
      .vs-table .vs-plate { left: 20px; right: auto; bottom: 16px; max-width: 54vw; }
      .rooms .room-table :is(th, td):is(:nth-child(3), :nth-child(4)) { display: none; }
      .rooms .room-table :is(th, td) { padding-left: 6px; padding-right: 6px; }
      .rooms .room-table code { word-break: break-all; }
      .rooms .room-table td.room-actions { display: table-cell; text-align: right; white-space: nowrap; }
      .rooms .room-table td.room-actions button + button { margin-left: 6px; }
    }

    .landing { text-align: center; padding: 72px 24px; }
    .landing h1 { margin: 0 0 28px; }
    .landing h1 img { display: block; width: min(320px, 80vw); height: auto; margin: 0 auto; }
    .landing .sign-in { font-size: 16px; padding: 11px 30px; }

    .lobby p { margin: 0 0 16px; color: var(--text-dim); }
    .lobby h3 { font-size: 16px; font-weight: 700; color: var(--text-strong); margin: 26px 0 8px; }
    .lobby h3 .count { font-weight: 400; color: var(--text-dim); margin-left: 4px; }

    /* Purple for the thing to do next, charcoal for the rest, red for removing someone. */
    .lobby button, .room-table button, .btn {
      font: inherit; font-size: 14px; font-weight: 600; line-height: 1.2; padding: 8px 16px; cursor: var(--cursor-pointer, pointer); border-radius: 5px; border: 0;
      color: var(--text-strong); background: var(--brand); transition: background 150ms;
    }
    .lobby button:hover:not(:disabled), .room-table button:hover:not(:disabled), .btn:hover:not(:disabled) { background: var(--brand-hi); }
    .lobby button:disabled { background: var(--surface-3); color: var(--text-faint); cursor: var(--cursor-default, not-allowed); }
    .lobby button.secondary, .lobby button.small, .room-table button.watch, .btn.secondary { font-weight: 500; background: var(--surface-3); color: var(--text); }
    .lobby button.secondary:hover:not(:disabled), .lobby button.small:hover:not(:disabled), .room-table button.watch:hover:not(:disabled), .btn.secondary:hover:not(:disabled) { background: #3d3d3d; }
    .lobby button.danger { font-weight: 500; background: none; color: var(--bad); box-shadow: inset 0 0 0 1px rgb(229 72 77 / 0.45); }
    .lobby button.danger:hover:not(:disabled) { background: var(--bad-soft); }
    .lobby button.small { padding: 4px 10px; font-size: 12.5px; }

    .lobby-row { margin: 12px 0; display: flex; align-items: center; flex-wrap: wrap; gap: 8px 10px; }
    .lobby-row.actions { margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--border); gap: 10px; }
    .lobby-label { min-width: 120px; color: var(--text-dim); }
    .lobby select, .prompt select {
      font: inherit; padding: 6px 30px 6px 10px; border-radius: 3.5px; border: 1px solid #000; color: #bbb; cursor: var(--cursor-pointer, pointer);
      appearance: none; -webkit-appearance: none;
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23bbbbbb' stroke-width='1.5'/%3E%3C/svg%3E") no-repeat right 10px center, var(--input);
    }
    .lobby select:disabled { opacity: 0.5; cursor: var(--cursor-default, not-allowed); }
    .lobby label { cursor: var(--cursor-pointer, pointer); user-select: none; display: inline-flex; align-items: center; gap: 8px; color: var(--text); }
    .opp-deck { color: var(--text-dim); }
    .deck-editor-link { margin-left: 4px; }
    .ready-badge, .host-badge, .ai-badge { font-size: 11px; font-weight: 500; line-height: 1; padding: 4px 7px; border-radius: 5.25px; }
    .ready-badge { background: var(--good-soft); color: var(--good); }
    .host-badge { background: var(--tag); color: var(--text-strong); }
    .ai-badge { background: var(--lavender-soft); color: var(--lavender); }
    .hint { color: var(--text-faint); font-size: 13px; }
    .none { color: var(--text-faint); }

    .room-table { width: 100%; border-collapse: collapse; }
    .room-table th { text-align: left; padding: 8px 10px; border-bottom: 1px solid var(--border); color: var(--text-dim); font-weight: 600; font-size: 13px; }
    .room-table td { padding: 9px 10px; border-bottom: 1px solid var(--border); }
    .room-table tbody tr:hover { background: var(--surface-2); }
    .room-table button { padding: 6px 12px; font-size: 13px; }
    .room-table button:disabled { background: var(--surface-3); color: var(--text-faint); cursor: var(--cursor-default, not-allowed); }
    .current-room { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px 16px; margin: 26px 0 0; padding: 14px 16px; border: 1px solid var(--border-strong); border-left: 3px solid var(--action-orange); border-radius: 6px; background: var(--surface-2); }
    .current-room p { margin: 0; color: var(--text); }
    .current-room code { font-family: inherit; color: var(--text-strong); }
    .room-actions { display: flex; gap: 6px; justify-content: flex-end; }
    .room-table code { font-family: inherit; color: var(--text-strong); }

    .dropped-note { color: var(--lavender); font-size: 12.5px; margin-right: 8px; }
    .board-menu { display: flex; flex-direction: column; font-size: 13px; color: var(--text-dim); }
    .board-menu section { display: flex; flex-direction: column; align-items: flex-start; gap: 10px; padding: 14px 16px; border-bottom: 1px solid var(--border); }
    .board-menu h3 { margin: 0; font-size: 12px; font-weight: 700; color: var(--text-strong); }
    .board-menu .setting { display: flex; align-items: flex-start; gap: 10px; }
    .board-menu .hint { font-size: 12px; }
    .board-menu .setting-options input { margin-right: 6px; }
    .board-menu dl { display: grid; grid-template-columns: auto 1fr; gap: 4px 14px; margin: 0; width: 100%; }
    .board-menu dt { color: var(--text-faint); }
    .board-menu dd { margin: 0; color: var(--text); font-variant-numeric: tabular-nums; }
    .menu-links { display: flex; gap: 16px; align-items: baseline; }
    .reconnecting {
      position: fixed; left: 50%; top: 14px; transform: translateX(-50%); z-index: 200;
      background: var(--surface-2); border: 1px solid var(--border-strong); color: var(--lavender);
      padding: 8px 16px; border-radius: 6px; font-weight: 600; box-shadow: 0 10px 30px rgb(0 0 0 / 0.5);
    }
    .error { background: #3a1416; border: 1px solid var(--bad); color: #f5c2c4; padding: 9px 14px; border-radius: 6px; margin: 0 0 14px; }
    /* The end of a game: the result over a blurred blow-up of the winner's key card, then each seat. */
    .winner {
      position: relative; isolation: isolate; overflow: hidden; margin-top: 6px; min-height: 200px; padding: 34px 40px; border-radius: 8px;
      display: flex; align-items: center; justify-content: space-between; gap: 24px;
      font-size: 58px; font-weight: 700; line-height: 1.05; color: var(--text-strong); background: var(--surface-3);
    }
    .winner::before { content: ""; position: absolute; inset: -40px; z-index: -2; background: var(--art, none) center 22% / cover no-repeat; filter: blur(16px) saturate(1.2); opacity: 0.6; }
    .winner::after { content: ""; position: absolute; inset: 0; z-index: -1; background: linear-gradient(100deg, rgb(115 31 161 / 0.94) 0%, rgb(82 21 120 / 0.78) 45%, rgb(44 12 66 / 0.3) 100%); }
    .winner.defeat::after { background: linear-gradient(100deg, rgb(107 29 34 / 0.94) 0%, rgb(74 20 24 / 0.78) 45%, rgb(42 12 14 / 0.3) 100%); }
    .winner.draw::after { background: linear-gradient(100deg, rgb(51 51 51 / 0.95) 0%, rgb(40 40 40 / 0.6) 100%); }
    .winner small { display: block; margin-top: 10px; font-size: 16px; font-weight: 400; line-height: 1.4; color: rgb(255 255 255 / 0.85); }
    .winner-card { flex: none; --card-w: 150px; --card-h: 210px; margin: -12px 12px -12px 0; transform: rotate(4deg); filter: drop-shadow(0 18px 30px rgb(0 0 0 / 0.6)); }
    .final-seats { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 14px; margin-top: 18px; }
    .final-seat { padding: 16px 18px 18px; border-radius: 8px; background: var(--surface-2); border: 1px solid var(--border); }
    .final-seat.won { border-color: var(--lavender); }
    .final-seat header { display: flex; align-items: center; gap: 12px; }
    .seat-face { flex: none; width: 38px; height: 38px; border-radius: 50%; display: grid; place-items: center; font-size: 16px; font-weight: 700; color: var(--text-strong); background: #4a4a4a; }
    .seat-face.you { background: var(--brand); }
    .seat-who { flex: 1; min-width: 0; display: flex; flex-direction: column; line-height: 1.3; }
    .seat-who b { font-size: 15px; font-weight: 600; color: var(--text-strong); }
    .seat-deck { font-size: 13px; color: var(--text-dim); }
    .seat-tag { font-size: 11px; font-weight: 500; line-height: 1; padding: 4px 7px; border-radius: 5.25px; background: var(--surface-3); color: var(--text-dim); }
    .seat-tag.won { background: var(--lavender-soft); color: var(--lavender); }
    .numbers { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 8px; margin: 16px 0 14px; }
    .numbers dt { font-size: 12px; color: var(--text-dim); white-space: nowrap; }
    .numbers dd { margin: 2px 0 0; font-size: 22px; font-weight: 700; line-height: 1.1; color: var(--text-strong); font-variant-numeric: tabular-nums; }
    .final-cards { display: flex; flex-wrap: wrap; gap: 6px; --card-w: 64px; --card-h: 90px; }
    .final-seat .none { margin: 0; font-size: 13px; }
    .room-actions-after { margin-top: 16px; justify-content: center; }
    .watch-speed { display: inline-flex; flex-wrap: wrap; gap: 4px; align-items: center; }
    .watch-speed .speed-btn { font: inherit; font-size: 13px; font-weight: 500; padding: 5px 10px; min-width: 40px; cursor: var(--cursor-pointer, pointer); line-height: 1.2; border-radius: 5px; border: 0; background: var(--surface-3); color: var(--text); }
    .watch-speed .speed-btn:hover:not(:disabled) { background: #3d3d3d; }
    .watch-speed .speed-btn.on { background: var(--brand); color: var(--text-strong); font-weight: 600; }
    .watch-speed .speed-btn:disabled { opacity: 0.45; cursor: var(--cursor-default, not-allowed); }
    /* Above the table, which takes the rest of the page. */
    .watch-bar { flex: none; display: flex; gap: 12px; align-items: center; flex-wrap: wrap; justify-content: center; padding: 7px 16px; font-size: 13px; color: var(--text-dim); background: var(--bg); border-bottom: 1px solid var(--border); }
    .watch-bar b { color: var(--text-strong); }
    .watch-bar .btn { font-size: 13px; padding: 5px 12px; }
    :host([at-table]) { height: 100%; }
    :host([at-table]) .room { height: 100%; display: flex; flex-direction: column; }
    :host([at-table]) arena-board { flex: 1; min-height: 0; }
    .rotate-hint { display: none; }
    @media (orientation: portrait) and (max-width: 600px) {
      .rotate-hint {
        position: fixed; inset: 0; z-index: 1000; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px;
        padding: 24px; text-align: center; background: var(--bg); color: var(--text-dim);
      }
      .rotate-hint svg { color: var(--text-strong); animation: rotate-hint 2.4s ease-in-out infinite; }
      .rotate-hint b { color: var(--text-strong); font-size: 20px; font-weight: 700; }
      .rotate-hint span { max-width: 280px; font-size: 14px; }
      .rotate-hint .btn { margin-top: 8px; }
    }
    @keyframes rotate-hint { 0%, 20% { transform: rotate(0deg); } 50%, 80% { transform: rotate(-90deg); } 100% { transform: rotate(0deg); } }
  `];

  // A tab's link swaps its page in, without loading the page again; a room's link still loads, so
  // the room starts clean.
  private readonly onLinkClick = (e: MouseEvent): void => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (this.connState !== "browsing-rooms") return;
    const link = e.composedPath().find((n): n is HTMLAnchorElement => n instanceof HTMLAnchorElement);
    if (!link || link.target !== "" || link.hasAttribute("download")) return;
    const to = new URL(link.href);
    const page = pageAt(to);
    if (page === null || to.searchParams.has("game")) return;
    if (to.hash !== "" && to.pathname === location.pathname && to.search === location.search) return;
    e.preventDefault();
    this.showPage(page, to, location.href);
  };

  private readonly onPopState = (): void => {
    if (this.connState === "browsing-rooms") this.showPage(pageAt(new URL(location.href)) ?? "home", null, "");
  };

  // Cross-fades to the page, after adding `pushed` to the history when it's a new visit.
  private showPage(page: TopNavTab, pushed: URL | null, ref: string): void {
    const swap = async (): Promise<void> => {
      if (pushed) {
        history.pushState(null, "", pushed);
        window.scrollTo(0, 0);
      }
      recordPageHit(ref);
      this.page = page;
      this.editingName = false;
      if (page === "home") void this.loadHomeDecks();
      if (page === "profile") void this.loadGameHistory(0);
      await this.updateComplete;
    };
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) void swap();
    else document.startViewTransition(swap);
  }

  connectedCallback(): void {
    super.connectedCallback();
    recordPageHit(document.referrer);
    installClickFeedback();
    window.addEventListener("click", this.onLinkClick);
    window.addEventListener("popstate", this.onPopState);
    window.addEventListener("pointerdown", this.onPress, { capture: true });
    window.addEventListener("keydown", this.onPress, { capture: true });
    window.addEventListener("click", this.onClickAfterSkip, { capture: true });
    this.addEventListener(REPORT_ISSUE, this.onReportIssue);
    // The deck editor's ?game= is the room to go back to.
    this.game = this.page === "decks" ? "" : readUrlParams().game;
    this.start().catch((err) => {
      this.connState = "signed-out";
      this.startupError = `Couldn't check sign-in: ${err instanceof Error ? err.message : String(err)}`;
    });
  }

  // /me first, with dev auth's ?user= if there is one: it says how this page signs in. With an
  // AuthKit client, a session AuthKit holds (or the /callback that just made one) signs in, and a
  // visitor without one goes straight to AuthKit's sign-in. A /callback that made no session stays
  // put, so a failed sign-in shows "Sign in" rather than looping.
  private async start(): Promise<void> {
    const devUser = readUrlParams().user;
    const me = await fetchMe(devUser ? { kind: "dev-user", user: devUser } : null);
    this.authMode = me.mode;
    if (me.authenticated) {
      this.signedIn({ kind: "named", user: me.user_id }, me);
      return;
    }
    if (!me.client_id) {
      this.connState = "signed-out";
      return;
    }
    let returning = false;
    const authkit = await startAuthKit(me.client_id, (path) => {
      returning = true;
      this.connState = "returning";
      location.replace(path);
    });
    this.authkit = authkit;
    if (returning) return;
    if (location.pathname === CALLBACK_PATH) {
      this.connState = "signed-out";
      return;
    }
    if (authkit.getUser() === null) {
      this.connState = "signing-in";
      await signIn(authkit);
      return;
    }
    const session: Session = { kind: "authkit", authkit };
    let verified: MeResponse;
    try {
      verified = await fetchMe(await freshCredential(session));
    } catch (err) {
      this.signInError = err instanceof Error ? err.message : String(err);
      this.connState = "signed-out";
      return;
    }
    if (!verified.authenticated) {
      this.connState = "signed-out";
      return;
    }
    this.signedIn(session, verified);
  }

  private signedIn(session: Session, me: MeAuthenticated): void {
    this.session = session;
    this.user = me.user_id;
    this.displayName = me.display_name;
    this.isAdmin = me.is_admin === true;
    this.learning = readLearning(this.user);
    if (!this.game && !this.learning.tourSeen) this.tourIndex = 0;
    if (!this.game) {
      this.connState = "browsing-rooms";
      void this.loadRooms();
      void this.loadHomeDecks();
      if (this.page === "profile") void this.loadGameHistory(0);
      this.roomsPollTimer = window.setInterval(() => void this.loadRooms(), 3000);
    } else {
      this.connState = "connecting";
      this.openWire();
    }
    void this.loadDecks();
    void this.loadProgress();
  }

  private async loadProgress(): Promise<Progress | null> {
    try {
      const r = await this.api("me/progress");
      if (!r.ok) return null;
      this.progress = progressOf(await r.json());
      return this.progress;
    } catch {
      return null;
    }
  }

  // The room records a game's results as it ends, so the XP it earned may take a moment to count.
  private async loadProgressEarned(): Promise<void> {
    const before = this.progress;
    if (!before) return;
    for (const waitMs of [1500, 4000]) {
      await new Promise((done) => window.setTimeout(done, waitMs));
      const after = await this.loadProgress();
      if (after && after.xp > before.xp) {
        this.progressEarned = { before, after };
        return;
      }
    }
  }

  private async choose(cosmetic: Cosmetic, id: string | null): Promise<Progress | string> {
    try {
      const r = await this.api(`me/${cosmetic}`, id === null ? { method: "DELETE" } : {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      const read = await r.json();
      if (!r.ok) return (read as { error?: string }).error ?? `HTTP ${r.status}`;
      return progressOf(read);
    } catch (err) {
      return err instanceof Error ? err.message : String(err);
    }
  }

  // The 483 card backs, fetched once, when Settings first shows them.
  private findCardBacks(): Promise<CardBack[]> {
    this.cardBacks ??= this.api("card-backs").then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return (await r.json() as { card_backs: CardBack[] }).card_backs;
    }).catch((err: unknown) => {
      this.cardBacks = null;
      throw err;
    });
    return this.cardBacks;
  }

  // Once, when the seats are known: the card backs, frames and name plates their players chose.
  private async loadSeatLooks(): Promise<void> {
    const people = this.pregame.filter((p) => !p.user_id.startsWith("ai:"));
    const asked = people.map((p) => p.user_id).sort().join(",");
    if (asked === "" || asked === this.seatLooksAskedFor) return;
    this.seatLooksAskedFor = asked;
    try {
      const r = await this.api(`table-cosmetics?users=${encodeURIComponent(asked)}`);
      if (!r.ok) return;
      const { players } = await r.json() as TableCosmeticsJson;
      this.seatLooks = Object.fromEntries(players.flatMap((row) => {
        const seat = people.find((p) => p.user_id === row.user_id)?.seat;
        return seat ? [[seat, seatLooksOf(row)]] : [];
      }));
    } catch { /* the table's own backs, frames and names */ }
  }

  // A request as the signed-in player, relative to the release's own base.
  private async api(path: string, init: RequestInit = {}): Promise<Response> {
    if (!this.session) throw new SignedOut("not signed in");
    const [url, authorized] = authorizedRequest(path, await freshCredential(this.session), init);
    return fetch(url, authorized);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("click", this.onLinkClick);
    window.removeEventListener("popstate", this.onPopState);
    window.removeEventListener("pointerdown", this.onPress, { capture: true });
    window.removeEventListener("keydown", this.onPress, { capture: true });
    window.removeEventListener("click", this.onClickAfterSkip, { capture: true });
    this.removeEventListener(REPORT_ISSUE, this.onReportIssue);
    this.updates.forget();
    this.wire?.stop();
    if (this.roomsPollTimer !== null) {
      window.clearInterval(this.roomsPollTimer);
      this.roomsPollTimer = null;
    }
    if (this.matchIntroTimer !== null) {
      window.clearTimeout(this.matchIntroTimer);
      this.matchIntroTimer = null;
    }
    if (this.dropTimer !== null) {
      window.clearTimeout(this.dropTimer);
      this.dropTimer = null;
    }
  }

  private async loadRooms(): Promise<void> {
    try {
      const r = await this.api("rooms");
      const json = await r.json() as { rooms?: RoomSummary[]; current?: Option<CurrentRoom>; online?: number };
      this.rooms = json.rooms ?? [];
      this.online = json.online ?? null;
      this.currentRoom = json.current?.kind === "Some" ? json.current.values[0] : null;
    } catch { /* keep stale list on transient failures */ }
  }

  // The server mints the new game's id; the client never makes one up.
  private async createRoom(lesson?: Tutorial): Promise<void> {
    this.creatingRoom = true;
    this.lastError = null;
    try {
      const r = await this.api("games", { method: "POST" });
      if (!r.ok) throw new Error(await r.text());
      const { game_id } = await r.json() as { game_id: string };
      const url = new URL(location.href);
      if (lesson) url.searchParams.set("tutorial", lesson.id); else url.searchParams.delete("tutorial");
      url.searchParams.set("game", game_id);
      url.hash = "";
      location.assign(url);
    } catch (err) {
      this.lastError = `Couldn't create a room: ${err instanceof Error ? err.message : String(err)}`;
      this.creatingRoom = false;
    }
  }

  private joinRoom(id: string): void {
    this.navigateToRoom(id);
  }

  private rejoinCurrentRoom(current: CurrentRoom): void {
    if (current.release === pageRelease() || pageRelease() === null) {
      this.navigateToRoom(current.game_id);
      return;
    }
    const url = new URL(`/r/${current.release}/`, location.origin);
    const { user } = readUrlParams();
    if (user) url.searchParams.set("user", user);
    url.searchParams.set("game", current.game_id);
    location.assign(url);
  }

  private async leaveCurrentRoom(current: CurrentRoom): Promise<void> {
    const session = this.session;
    if (!session || !confirm(current.started ? "Leave this game? You concede it." : "Leave this room?")) return;
    this.leavingCurrentRoom = true;
    this.lastError = null;
    const wire = new Wire({
      game: current.game_id,
      fresh_credential: (refresh) => freshCredential(session, refresh),
      on_message: () => {},
      on_status: () => {},
    });
    wire.leaveRoom();
    try {
      for (let polls = 0; polls < 20 && this.currentRoom?.game_id === current.game_id; polls++) {
        await new Promise((done) => window.setTimeout(done, 500));
        await this.loadRooms();
      }
    } finally {
      wire.stop();
      this.leavingCurrentRoom = false;
    }
    if (this.currentRoom?.game_id === current.game_id) this.lastError = "Couldn't leave that game. Rejoin it and leave from there.";
  }

  // Without a seat: the room connects this player as a spectator.
  private watchRoom(id: string): void {
    this.navigateToRoom(id, true);
  }

  private navigateToRoom(id: string, watch = false): void {
    const url = new URL(location.href);
    url.searchParams.set("game", id);
    if (watch) url.searchParams.set("watch", "1"); else url.searchParams.delete("watch");
    url.hash = "";
    // Reload so the WS connection, lobby state and decks all re-init cleanly.
    location.assign(url);
  }

  // The deck editor, open on the deck picked here, checked as this room checks decks, with a way
  // back to this room.
  // The deck editor open on one deck, or on its list for none.
  private deckEditorHrefFor(deckId: string | null): string {
    const params = new URLSearchParams();
    const { user } = readUrlParams();
    if (user) params.set("user", user);
    if (deckId) params.set("deck", deckId);
    return `deck-editor?${params}`;
  }

  private deckEditorHref(): string {
    const params = new URLSearchParams();
    const { user } = readUrlParams();
    if (user) params.set("user", user);
    if (this.game) params.set("game", this.game);
    const picked = this.availableDecks?.find((d) => d.name === this.myPickedDeck());
    if (picked) params.set("deck", picked.deck_id);
    if (this.strict) params.set("rules", "strict");
    return `deck-editor?${params}`;
  }

  private goToRoomBrowser(): void {
    const url = new URL(location.href);
    url.searchParams.delete("game");
    url.searchParams.delete("tutorial");
    url.searchParams.delete("watch");
    url.hash = "";
    if (/^\/r\/\d+\//.test(url.pathname) && !LOCAL_HOSTS.includes(url.hostname)) {
      url.pathname = "/";
      if (url.search === "") url.searchParams.set("rooms", "1");
    }
    location.assign(url);
  }

  // The player's decks with the cards that front them, for home and the profile.
  private async loadHomeDecks(): Promise<void> {
    try {
      const r = await this.api("decks");
      const { decks = [] } = await r.json() as { decks?: ListedHomeDeck[] };
      this.homeDecks = decks.map(homeDeckOf);
    } catch {
      this.homeDecks = [];
    }
  }

  private get heroDecks(): HomeDeck[] | null {
    return this.homeDecks?.slice(0, 8) ?? null;
  }

  // Page 0 starts the list afresh; a later page adds the older games under it.
  private async loadGameHistory(page: number): Promise<void> {
    if (page === 0) {
      this.gameHistory = null;
      this.gameHistoryError = null;
    } else {
      this.loadingMoreHistory = true;
    }
    try {
      const r = await this.api(`history?page=${page}`);
      if (!r.ok) throw new Error(await r.text());
      const read = await r.json() as HistoryPage;
      this.gameHistory = page === 0 || this.gameHistory === null ? read : { ...read, games: [...this.gameHistory.games, ...read.games] };
    } catch (err) {
      this.gameHistoryError = `Couldn't load your games: ${err instanceof Error ? err.message : String(err)}`;
    } finally {
      this.loadingMoreHistory = false;
    }
  }

  private showHeroDeck(index: number): void {
    const count = Math.max(1, this.heroDecks?.length ?? 0) + 1;
    this.heroIndex = ((index % count) + count) % count;
  }

  private async loadDecks(): Promise<void> {
    try {
      const r = await this.api("decks");
      const json = await r.json() as { decks?: DeckChoice[] };
      this.availableDecks = json.decks ?? [];
    } catch {
      this.availableDecks = null;
    }
  }

  private async saveDisplayName(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    const input = (e.target as HTMLFormElement).elements.namedItem("display_name") as HTMLInputElement;
    try {
      const r = await this.api("me/display-name", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_name: input.value }),
      });
      if (!r.ok) {
        this.nameError = await r.text();
        return;
      }
      const me = await r.json() as MeAuthenticated;
      this.displayName = me.display_name ?? me.user_id;
      this.editingName = false;
      this.nameError = null;
    } catch (err) {
      this.nameError = err instanceof Error ? err.message : String(err);
    }
  }

  private signInWithAuthKit(): void {
    if (this.authkit) void signIn(this.authkit);
  }

  private signOutEverywhere(): void {
    if (this.session?.kind === "authkit") {
      this.wire?.stop();
      signOut(this.session.authkit);
    }
  }

  private openWire(): void {
    const session = this.session;
    if (!session) return;
    this.wire = new Wire({
      game: this.game,
      watch: readUrlParams().watch,
      fresh_credential: (refresh) => freshCredential(session, refresh),
      on_message: (msg) => this.onMessage(msg),
      on_status: (status) => this.onWireStatus(status),
      on_round_trip: (ms) => { this.roundTripMs = Math.round(ms); },
      server_is_busy: () => this.replayLoading,
    });
  }

  private onWireStatus(status: WireStatus): void {
    switch (status.kind) {
      case "connecting":
        this.connState = "connecting";
        break;
      case "open":
        this.connState = "open";
        if (!this.gameState && !this.spectating) this.lobbyMessage = "Connected. Click Ready when set.";
        break;
      case "reconnecting":
        this.connState = "reconnecting";
        if (this.replayLoading) {
          this.replayFrames = [];
          this.replayTotal = 0;
          this.replayLoading = false;
          this.replayError = null;
        }
        break;
      case "banned":
        this.connState = "banned";
        return;
      case "replaced":
        this.connState = "replaced";
        break;
      case "game-gone":
        // A finished game's room forgets it; its review stays, from the saved log.
        if (this.ended) this.connState = "archived";
        else void this.reviewArchived();
        break;
      case "in-another-game":
        this.goToRoomBrowser();
        break;
      case "signed-out":
        this.connState = "signed-out";
        break;
      case "gave-up":
        this.connState = "gave-up";
        break;
    }
  }


  private onMessage(msg: ServerMsg): void {
    switch (msg.kind) {
      case "JoinAccepted": {
        const seatOpt = msg.values[1];
        if (seatOpt.kind === "Some") this.seat = seatOpt.values[0];
        this.spectating = false;
        if (!this.gameState) this.lobbyMessage = "Joined. Pick a deck.";
        break;
      }
      case "WatchAccepted": {
        this.spectating = true;
        this.seat = null;
        this.lobbyMessage = "";
        break;
      }
      case "LobbyUpdate": {
        this.tutorialSetupPending = false;
        this.lobby = msg.values[0];
        if (this.leaving && !this.lobby.some((u) => u.user_id === this.user)) this.goToRoomBrowser();
        const receivedAt = Date.now();
        this.dropsAt = new Map(this.lobby.flatMap((u) => u.drops_in_ms.kind === "Some" ? [[u.user_id, receivedAt + u.drops_in_ms.values[0]] as const] : []));
        this.noteDrops();
        const gt = msg.values[1];
        this.gameType = gt.kind === "Commander" ? "Commander" : "Standard";
        this.maxPlayers = msg.values[2];
        this.strict = msg.values[3];
        this.exhibition = msg.values[4];
        this.spectators = msg.values[5];
        this.scenario = msg.values[6].kind === "Some" ? msg.values[6].values[0] : null;
        this.mulligans = msg.values[7].kind;
        this.clockSetting = msg.values[8];
        this.tutorial = tutorials.find((lesson) => lesson.scenario === this.scenario) ?? this.tutorial;
        if (this.myPickedDeck() === null) this.deckWarnings = [];
        void this.loadUrlScenario();
        break;
      }
      case "WatchSpeedUpdate": {
        this.watchSpeed = msg.values[0];
        this.mayChangeWatchSpeed = msg.values[1];
        break;
      }
      case "PregameUpdate": {
        this.pregame = msg.values[0];
        if (this.gameState) this.gameState = withDisplayNames(this.gameState, this.pregame);
        void this.loadSeatLooks();
        break;
      }
      case "WaitingForReady": {
        if (!this.gameState) this.lobbyMessage = "Both players here — pick a deck and Ready up.";
        break;
      }
      case "GameStarted": {
        this.lobbyMessage = "";
        this.looks = {};
        this.matchIntro = true;
        playSound("dice-roll");
        this.matchIntroTimer = window.setTimeout(() => {
          this.matchIntro = false;
          this.matchIntroTimer = null;
        }, MATCH_INTRO_MS);
        break;
      }
      case "StateUpdate": {
        const [seat, state, update, moves] = msg.values;
        const history = historyAfter(this.received?.history ?? [], update);
        if (history === null) {
          this.wire?.resync();
          break;
        }
        // A spectator's StateUpdate names no seat.
        const sent: ShownUpdate = { seat: seat === "" ? null : seat, state, history, moves, walking: null };
        if (update.kind === "WholeHistory") this.updates.forget();
        // A player is shown each step the Room passed through for them; a spectator follows the Room's pace.
        else if (this.received && sent.seat !== null && stepBeatMs(this.speed()) > 0) for (const step of stepsPassed(this.received, sent)) this.updates.receive(step);
        this.received = sent;
        this.updates.receive(sent);
        this.catchingUp = this.updates.behind;
        break;
      }
      case "ReplayUpdate": {
        const [start, total, cards, frames] = msg.values;
        if (start !== this.replayFrames.length) {
          this.replayLoading = false;
          this.replayError = "Replay transfer was interrupted. Try loading it again.";
          break;
        }
        if (start === 0) this.replayCards = [];
        this.replayCards.push(...cards);
        this.replayFrames = [...this.replayFrames, ...frames.map((f) => replayPosition(this.replayCards, f))];
        this.replayTotal = total;
        this.replayLoading = this.replayFrames.length < total;
        break;
      }
      case "ErrorMsg": {
        this.tutorialSetupPending = true;
        this.lastError = msg.values[0];
        if (this.replayLoading) {
          this.replayLoading = false;
          this.replayError = msg.values[0];
        }
        this.autoPaying = false;
        break;
      }
      case "DeckWarnings": {
        this.deckWarnings = msg.values[1];
        break;
      }
      case "PacingUpdate": {
        this.pacing = msg.values[0];
        break;
      }
      case "UndoUpdate": {
        this.undoable = msg.values[0];
        break;
      }
      case "ClockUpdate": {
        this.clocks = { clocks: msg.values[0], receivedAt: performance.now() };
        break;
      }
      case "LookUpdate": {
        this.looks = { ...this.looks, [msg.values[0]]: msg.values[1] };
        break;
      }
    }
  }

  private showUpdate(u: ShownUpdate): void {
    this.playSoundsOf(u);
    const decision = u.state.decision;
    this.seat = u.seat;
    this.gameState = withDisplayNames(u.state, this.pregame);
    this.history = u.history;
    this.moves = u.moves;
    this.walking = u.walking;
    this.catchingUp = this.updates.behind;
    this.lastError = null;
    this.autoPaying = false;
    const question = decision.kind === "ViewerChooses" ? decision.values[0] : null;
    const autoPaid = question && this.autoPay ? autoPayAnswer(question) : null;
    if (autoPaid) {
      this.autoPaying = true;
      this.wire?.choiceAnswer(autoPaid);
    }
    const endedNow = decision.kind === "GameEnded" && !this.ended;
    this.ended = decision.kind === "GameEnded";
    if (endedNow && !this.spectating) void this.loadProgressEarned();
    this.winner = decision.kind === "GameEnded" ? optionValue(decision.values[0]) : null;
    if (this.ended && this.replayFrames.length === 0 && !this.replayLoading && !this.replayError) this.loadReplay();
    if (this.leaving && this.isOutOfGame()) this.goToRoomBrowser();
  }

  private isOutOfGame(): boolean {
    return this.ended || (this.seat !== null && this.gameState?.players[this.seat]?.lost === true);
  }

  private playSoundsOf(u: ShownUpdate): void {
    const before = this.lastShown;
    this.lastShown = u;
    if (before && startsTurn(u, before)) playSound("turn");
    const sound = soundOfMoves(u.moves);
    if (sound) playSound(sound);
  }

  // An update with an AI seat's action stays a beat, each step the Room passed through a shorter
  // one, and the first of a new turn long enough to read its banner, at the room's AI speed.
  private beatAfter(u: ShownUpdate, before: ShownUpdate | null): number {
    const turn = before && startsTurn(u, before) ? turnBeatMs(this.speed()) : 0;
    return Math.max(turn, this.stepBeatAfter(u, before));
  }

  private stepBeatAfter(u: ShownUpdate, before: ShownUpdate | null): number {
    if (u.walking) return stepBeatMs(this.speed());
    if (!before) return 0;
    const ai = this.aiLevels();
    if (u.history.slice(before.history.length).some((e) => e.seat in ai && isAction(e.answer))) return beatMs(this.speed());
    return sameStep(stepOf(u.state), before.walking ?? stepOf(before.state)) ? 0 : stepBeatMs(this.speed());
  }

  private speed(): Speed["kind"] {
    return this.watchSpeed?.speed.kind ?? "Normal";
  }

  // While the board shows an update older than one waiting, a press on it or a key shows the latest
  // instead of acting on what has moved on; otherwise a press ends the beat, so whatever the player
  // does next shows at once.
  private readonly onPress = (e: PointerEvent | KeyboardEvent): void => {
    this.swallowClick = false;
    if (e instanceof KeyboardEvent && MODIFIER_KEYS.includes(e.key)) return;
    if (e instanceof PointerEvent && !e.composedPath().some((node) => node instanceof Element && node.localName === "arena-board")) return;
    if (!this.updates.behind) {
      this.updates.endBeat();
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    this.swallowClick = e instanceof PointerEvent;
    this.updates.showLatest();
  };

  private readonly onClickAfterSkip = (e: MouseEvent): void => {
    if (!this.swallowClick) return;
    this.swallowClick = false;
    e.stopPropagation();
    e.preventDefault();
  };

  private sendReady(): void { this.wire?.ready(); }

  // A dev Host's ?scenario=, once: a file in mtg/scenarios by name (kili for kili.scenario), or
  // the scenario's own text.
  private async loadUrlScenario(): Promise<void> {
    const wanted = readUrlParams().scenario;
    if (this.urlScenarioTried || !wanted || this.authMode !== "dev" || !this.myLobby()?.is_host) return;
    this.urlScenarioTried = true;
    try {
      const text = await scenarioText(wanted);
      this.scenarioDraft = text;
      this.wire?.setScenario(text);
    } catch {
      this.lastError = `There's no scenario ${wanted} in mtg/scenarios.`;
    }
  }
  private pickDeck(deckId: string): void {
    this.deckWarnings = [];
    this.wire?.pickDeck(deckId);
  }
  // Why this room would refuse the deck, as the server does, or null when it can be picked. A Casual
  // room plays a deck that breaks its format's rules, but a game against AI seats alone then earns
  // no XP.
  private deckRefusal(d: DeckChoice): string | null {
    if (d.unplayable.length > 0) return "unplayable cards";
    if (d.format !== this.gameType) return `${d.format} deck`;
    if (this.strict && d.problems.length > 0) return `breaks ${d.format} rules`;
    return null;
  }
  private deckLegal(d: DeckChoice): boolean {
    return this.deckRefusal(d) === null && d.problems.length === 0;
  }
  // Legal decks first, then those a Casual room plays though they break the rules, in red, then the
  // ones this room refuses, greyed out with the reason.
  private deckOptions(selected: (d: DeckChoice) => boolean): TemplateResult {
    const decks = this.availableDecks ?? [];
    const breaking = decks.filter((d) => this.deckRefusal(d) === null && d.problems.length > 0);
    const refused = decks.filter((d) => this.deckRefusal(d) !== null);
    return html`
      ${decks.filter((d) => this.deckLegal(d)).map((d) => html`<option value=${d.deck_id} ?selected=${selected(d)}>${d.name}</option>`)}
      ${breaking.length > 0 ? html`<optgroup label=${`Breaks ${this.gameType} rules · no XP against AI`}>
        ${breaking.map((d) => html`<option class="breaks-rules" value=${d.deck_id} ?selected=${selected(d)} title=${d.problems.join("; ")}>${d.name}</option>`)}
      </optgroup>` : ""}
      ${refused.length > 0 ? html`<optgroup label="Can't be picked in this room">
        ${refused.map((d) => html`<option value=${d.deck_id} ?selected=${selected(d)} disabled>${d.name} — ${this.deckRefusal(d)}</option>`)}
      </optgroup>` : ""}`;
  }
  private adjustMaxPlayers(delta: -1 | 1): void {
    const sizes = this.exhibition ? EXHIBITION_SIZES : ROOM_SIZES;
    const next = this.maxPlayers + delta;
    if (sizes.includes(next) && next >= this.lobby.length) this.wire?.setMaxPlayers(next);
  }
  private addAiSeat(): void {
    if (this.aiDeck && this.lobby.length < this.maxPlayers) {
      this.wire?.addAiSeat(this.aiDeck, this.aiLevel);
      this.aiSlotOpen = null;
    }
  }
  private removeAiSeat(userId: string): void { this.wire?.kickUser(userId); }
  private setAutoPay(on: boolean): void {
    this.autoPay = on;
    try { localStorage.setItem(AUTO_PAY_KEY, on ? "on" : "off"); } catch { /* the toggle still holds for this tab */ }
  }
  private setShowLooks(on: boolean): void {
    this.showLooks = on;
    try { localStorage.setItem(SHOW_LOOKS_KEY, on ? "on" : "off"); } catch { /* the toggle still holds for this tab */ }
  }
  private setPreviewAt(at: PreviewAt): void {
    this.previewAt = at;
    try { localStorage.setItem(PREVIEW_AT_KEY, at); } catch { /* the choice still holds for this tab */ }
  }
  private setSoundEffects(on: boolean): void {
    this.soundEffects = on;
    saveSoundEffects(on);
  }

  private myLobby(): LobbyUser | undefined {
    return this.lobby.find((u) => u.user_id === this.user);
  }

  private othersInLobby(): LobbyUser[] {
    return this.lobby.filter((u) => u.user_id !== this.user);
  }

  private myPickedDeck(): string | null {
    const me = this.myLobby();
    if (!me) return null;
    return me.deck.kind === "Some" ? me.deck.values[0] : null;
  }

  render(): TemplateResult {
    if (this.startupError) return html`<div class="shell"><div class="error">${this.startupError}</div></div>`;
    if (this.connState === "loading-auth") return html`<div class="shell">Checking sign-in…</div>`;
    if (this.connState === "signing-in" || this.connState === "returning") return html`<div class="shell">Signing in…</div>`;
    if (this.connState === "signed-out") return html`<div class="shell">${this.renderSignIn()}</div>`;
    if (this.connState === "browsing-rooms") return html`${this.renderPage(this.page)}${this.renderBugReport()}`;
    if (this.connState === "connecting") return html`<div class="shell">Connecting to ${this.game}…</div>`;
    if (this.connState === "banned") return html`<div class="shell"><div class="error">This account has been banned.</div>${this.renderSignOut()}</div>`;
    if (this.connState === "replaced") return html`<div class="shell"><div class="error">This game is open in another tab.</div></div>`;
    if (this.connState === "gave-up") return html`<div class="shell"><div class="error">Couldn't reconnect for 15 minutes, so the game has ended.</div></div>`;

    // Read-only while reconnecting: nothing sent now could reach the Room.
    const reconnecting = this.connState === "reconnecting";
    return html`
      ${reconnecting ? html`<div class="reconnecting" role="status">Reconnecting…</div>` : ""}
      <div class="room" ?inert=${reconnecting}>${this.renderRoom()}</div>
      ${this.renderBugReport()}
    `;
  }

  willUpdate(changed: PropertyValues<this>): void {
    if (changed.has("page")) document.title = PAGE_TITLES[this.page];
  }

  // The page stops scrolling while the board fills it (index.html's body.at-table).
  updated(): void {
    const atTable = this.atTable();
    this.toggleAttribute("at-table", atTable);
    document.body.classList.toggle("at-table", atTable);
    this.prepareTutorial();
    if (this.tutorial && this.scenario === this.tutorial.scenario && this.gameState && this.seat && tutorialStep(this.tutorial, this.gameState, this.seat, this.history) === 3
      && !this.learning.completed.includes(this.tutorial.id)) {
      this.learning = { ...this.learning, completed: [...this.learning.completed, this.tutorial.id] };
      saveLearning(this.user, this.learning);
    }

  }

  private atTable(): boolean {
    const state = this.gameState;
    return this.matchIntro || (state !== null && !this.ended && (this.connState === "open" || this.connState === "reconnecting"));
  }

  private renderRoom(): TemplateResult {
    if (this.matchIntro) return this.renderMatchIntro();
    const state = this.gameState;
    if (state && this.atTable()) return this.renderBoard(state);

    return html`
      ${renderTopNav({ open: null, displayName: this.displayName, online: this.online, links: topNavLinks(this.deckEditorHref()), admin: this.isAdmin })}
      <div class="shell">
        ${this.lastError ? html`<div class="error">${this.lastError}</div>` : ""}
        ${!state && this.tutorial ? html`<section class="learning" aria-label="Preparing tutorial">
          <h2>${this.tutorial.title}</h2><p>Preparing your practice table and AI partner…</p>
          ${this.availableDecks && !this.tutorialPartnerDeck() ? html`<p>No starter deck is available for the AI partner. Try loading the deck list again.</p>` : ""}
          <div class="learning-actions"><button class="secondary" @click=${() => { this.tutorialSetupPending = false; this.lastError = null; void this.loadDecks(); }}>Retry tutorial setup</button>
            <button class="secondary" @click=${() => this.exitTutorial()}>Exit lesson</button></div>
        </section>` : ""}
        ${state ? this.renderEnded(state) : this.renderLobby()}
      </div>
    `;
  }

  private renderPage(page: TopNavTab): TemplateResult {
    if (page === "decks") return html`${this.renderWalkthrough()}<deck-editor .session=${this.session} .displayName=${this.displayName} .admin=${this.isAdmin} .online=${this.online}></deck-editor>`;
    const nav = renderTopNav({ open: page, displayName: this.displayName, online: this.online, links: topNavLinks(this.deckEditorHref()), admin: this.isAdmin });
    if (page === "admin") return html`${nav}<div class="shell"><admin-page .session=${this.session}></admin-page></div>`;
    return html`${nav}<div class="shell">${this.renderWalkthrough()}${this.renderHome(page)}</div>`;
  }

  private renderHome(view: HomeView): TemplateResult {
    const error = this.lastError ? html`<div class="error">${this.lastError}</div>` : "";
    switch (view) {
      case "profile": return html`${error}${this.renderProfile()}`;
      case "settings": return html`${error}${this.renderSettings()}`;
      case "home": return html`${error}${this.renderLanding()}`;
    }
  }

  private prepareTutorial(): void {
    const lesson = this.tutorial;
    if (!lesson || !this.game || this.gameState || this.ended || this.connState !== "open" || this.tutorialSetupPending || !this.myLobby()?.is_host) return;
    if (this.scenario !== null && this.scenario !== lesson.scenario) return;
    if (this.lobby.some((player) => player.user_id !== this.user && player.ai.kind === "None")) return;
    if (this.scenario !== lesson.scenario) {
      this.tutorialSetupPending = true;
      this.wire?.setScenario(lesson.scenario);
    } else if (this.lobby.length === 1 && this.tutorialPartnerDeck()) {
      this.tutorialSetupPending = true;
      this.wire?.addAiSeat(this.tutorialPartnerDeck()!.deck_id, "Easy");
    } else if (this.lobby.length === 2 && !this.myLobby()?.ready) {
      this.tutorialSetupPending = true;
      this.wire?.ready();
    }
  }

  // The first deck the room counts legal, or failing that the first it plays at all.
  private tutorialPartnerDeck(): DeckChoice | undefined {
    const decks = this.availableDecks ?? [];
    return decks.find((d) => this.deckLegal(d)) ?? decks.find((d) => this.deckRefusal(d) === null);
  }

  private replayWalkthrough(): void {
    this.heroIndex = 0;
    this.tourIndex = 0;
    this.showPage("home", new URL(topNavLinks(this.deckEditorHref()).home, document.baseURI), location.href);
  }

  private closeWalkthrough(): void {
    this.tourIndex = -1;
    this.learning = { ...this.learning, tourSeen: true };
    saveLearning(this.user, this.learning);
  }

  private renderWalkthrough(): TemplateResult | "" {
    if (this.tourIndex < 0) return "";
    const step = tourSteps[this.tourIndex];
    return html`<spotlight-tour label="Site walkthrough" .walkthrough=${true}
      .resolveStep=${() => ({ id: `site-${this.tourIndex}`, title: step.title, text: step.text,
        progress: `Site walkthrough · ${this.tourIndex + 1} of ${tourSteps.length}`, target: this.renderRoot.querySelector(step.target) })}
      .dismiss=${() => this.closeWalkthrough()}
      .controls=${html`
        ${this.tourIndex > 0 ? html`<button class="secondary" @click=${() => this.tourIndex--}>Back</button>` : ""}
        <button data-next @click=${() => this.tourIndex === tourSteps.length - 1 ? this.closeWalkthrough() : this.tourIndex++}>${this.tourIndex === tourSteps.length - 1 ? "Finish walkthrough" : "Next"}</button>
        <button class="secondary" @click=${() => this.closeWalkthrough()}>Skip tour</button>`}>
    </spotlight-tour>`;
  }

  private async openLessons(): Promise<void> {
    this.lessonsOpen = true;
    await this.updateComplete;
    const panel = this.renderRoot.querySelector<HTMLElement>("#learning-lessons");
    panel?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth", block: "nearest" });
    const nextLesson = panel?.querySelector<HTMLButtonElement>(".lesson-row:not(.completed) .lesson-action:not(:disabled)")
      ?? panel?.querySelector<HTMLButtonElement>(".lesson-action:not(:disabled)");
    nextLesson?.focus({ preventScroll: true });
  }

  private renderLearningHero(): TemplateResult {
    const completed = tutorials.filter((lesson) => this.learning.completed.includes(lesson.id)).length;
    return html`<div class="hero-text">
      <h1>Learn to play</h1>
      <p class="hero-meta"><span class="tag">Tutorials</span><span>${completed} of ${tutorials.length} complete</span></p>
      <p class="learning-summary">${tutorials.length} guided lessons</p>
      <div class="learning-hero-actions">
        <button class="hero-link learning-cta" aria-expanded=${this.lessonsOpen} aria-controls="learning-lessons" @click=${() => void this.openLessons()}>
          ${completed === tutorials.length ? "View lessons" : completed > 0 ? "Continue learning" : "Start learning"}
        </button>
        <button class="link-button" aria-label="Replay site walkthrough" @click=${() => this.replayWalkthrough()}>Site walkthrough</button>
      </div>
    </div>
    <div class="hero-card" aria-hidden="true">
      <card-art name="Grizzly Bears" manaCost="{1}{G}" typeLine="Creature — Bear" pt="2/2"></card-art>
    </div>`;
  }

  private renderLearning(): TemplateResult {
    return html`<section id="learning-lessons" class="learning lesson-library" aria-label="Practice lessons" ?hidden=${!this.lessonsOpen}>
      <header class="lesson-library-header"><div><h2>Tutorials</h2>
        <p>Choose a lesson to practice with an AI opponent. No deck needed.</p></div>
        <button class="btn secondary" aria-label="Close lessons" @click=${() => { this.lessonsOpen = false; this.renderRoot.querySelector<HTMLButtonElement>(".learning-cta")?.focus(); }}>Close</button>
      </header>
      ${tutorials.map((lesson, index) => {
        const completed = this.learning.completed.includes(lesson.id);
        return html`<div class="lesson-row ${completed ? "completed" : ""}">
          <span class="lesson-number" aria-hidden="true">${completed ? "✓" : index + 1}</span>
          <div class="lesson-copy"><h3>${lesson.title}</h3><p>${lesson.summary}</p>${completed ? html`<span class="lesson-status">Completed</span>` : ""}</div>
          <button class="btn lesson-action" ?disabled=${this.creatingRoom || this.currentRoom !== null} @click=${() => void this.createRoom(lesson)}>${completed ? "Replay" : "Start lesson"}</button>
        </div>`;
      })}
      <footer class="lesson-library-footer"><span>Tutorials don’t affect your game record.</span>
        ${this.currentRoom ? html`<span>Rejoin or leave your current room to start a lesson.</span>` : ""}
        <span>Progress saved in this browser.</span></footer>
    </section>`;
  }

  private exitTutorial(): void {
    this.leaving = true;
    this.wire?.leaveRoom();
    window.setTimeout(() => this.goToRoomBrowser(), 1500);
  }

  private renderTutorialCoach(state: PublicState): TemplateResult | "" {
    const lesson = this.tutorial;
    if (!lesson || this.scenario !== lesson.scenario || !this.seat || this.spectating) return "";
    const step = tutorialStep(lesson, state, this.seat, this.history);
    return html`<spotlight-tour label="Tutorial guidance" .collapsed=${this.coachHidden}
      .resolveStep=${() => this.tutorialSpotlight(lesson, state, step)}
      .dismiss=${() => { this.coachHidden = true; }}
      .controls=${html`
        <button class="secondary" @click=${() => this.coachHidden = !this.coachHidden}>${this.coachHidden ? "Show guidance" : "Hide guidance"}</button>
        <button @click=${() => this.exitTutorial()}>${step === 3 ? "Finish lesson" : "Exit lesson"}</button>`}>
    </spotlight-tour>`;
  }

  private tutorialSpotlight(lesson: Tutorial, state: PublicState, step: number): SpotlightStep {
    const board = this.renderRoot.querySelector("arena-board")?.shadowRoot;
    const primary = board?.querySelector("button.primary") ?? null;
    const card = (selector: string, name: string) => [...(board?.querySelectorAll(selector) ?? [])]
      .find((element) => element.querySelector("card-art")?.getAttribute("name") === name) ?? null;
    const guidance = (id: string, title: string, text: string, target: Element | null, shownStep = step, keepClear: Element | null = null): SpotlightStep => ({
      id: `${lesson.id}-${id}`, title, text, target, keepClear, progress: shownStep === 3 ? lesson.title : `${lesson.title} · Step ${shownStep + 1} of 3`,
    });
    if (step === 3) return guidance("complete", "Lesson complete", "You did it. Finish this lesson to try another, or hide the guidance and keep practicing.",
      lesson.id === "playing-cards" ? card(".mine .perm", "Grizzly Bears") : board?.querySelector(".opps .life") ?? primary);
    const payment = board?.querySelector(".pop.payment");
    if (payment) return guidance("pay", "Pay the mana cost", "The suggested mana sources are selected. Click Pay to use them, or choose your own sources first.", primary);
    if (lesson.id === "playing-cards") {
      if (step === 0) return guidance("land", "Play your Forest", lesson.steps[0], card(".hand-card", "Forest"));
      if (step === 1) return guidance("creature", "Cast Grizzly Bears", lesson.steps[1], card(".hand-card", "Grizzly Bears"));
    }
    if (lesson.id === "targeting" && step < 2) {
      if (board?.querySelector(".table.aiming")) return guidance("target", "Choose the opposing creature", lesson.steps[1], card(".opps .perm", "Grizzly Bears"), 1);
      return guidance("spell", "Cast Lightning Bolt", "Click Lightning Bolt in your hand. Next, you’ll choose what it deals damage to.", card(".hand-card", "Lightning Bolt"));
    }
    if (lesson.id === "attacking" && step < 2) {
      if (state.decision.kind === "ViewerDeclaresAttackers") {
        if (primary?.getAttribute("aria-label")?.startsWith("Attack (")) return guidance("confirm-attack", "Confirm your attack", "Your Bears are selected. Click Attack (1) to send them at your opponent.", primary);
        return guidance("attacker", "Choose an attacker", "Click your Grizzly Bears to select them as an attacker.", card(".mine .perm", "Grizzly Bears"));
      }
      return guidance("combat", "Move to combat", lesson.steps[0], primary);
    }
    if ("focus" in lesson) {
      const focus: { title: string; card?: string } = lesson.focus[step];
      const choosing = state.decision.kind === "ViewerChooses";
      const prompt = board?.querySelector(".pop.prompt");
      if (choosing && prompt) return guidance(`choose-${step}`, focus.title, lesson.steps[step], prompt, step, prompt.querySelector(".choose-actions"));
      if (choosing && lesson.id === "state-based-actions") {
        return guidance("keep-legend", focus.title, lesson.steps[step], card(".mine .perm", lesson.focus[0].card));
      }
      if (board?.querySelector(".table.aiming")) {
        return guidance("target-player", "Choose your opponent", "Click your opponent’s portrait to target them.", board.querySelector(".opps .avatar.legal"));
      }
      if (lesson.id === "priority" && step === 0) {
        const hold = board?.querySelector('button[aria-label="Hold priority"]');
        if (hold?.getAttribute("aria-pressed") === "false") {
          return guidance("hold", "Hold priority", "Casting a spell normally passes priority straight away, so it resolves before you can respond. Turn on Hold (or hold Ctrl, ⌘ on a Mac, while you cast) to keep priority after Lightning Bolt goes on the stack.", hold);
        }
      }
      if (lesson.id === "priority" && step === 1 && state.stack.length === 0) {
        return guidance("missed", "Lightning Bolt already resolved", "Casting it passed priority, so it resolved before you could respond. Cast Shock to finish, or exit and replay the lesson with Hold turned on.", card(".hand-card", "Shock"));
      }
      const inHand = focus.card === undefined ? null : card(".hand-card", focus.card);
      if (inHand) return guidance(`cast-${step}`, focus.title, lesson.steps[step], inHand);
    }
    const waiting = primary?.hasAttribute("disabled");
    const resolveTitle = "focus" in lesson ? lesson.focus[step].title : lesson.id === "attacking" ? "Continue to combat damage" : "Let the spell resolve";
    return guidance(waiting ? "waiting" : `resolve-${step}`, waiting ? "Your practice partner is responding" : resolveTitle,
      waiting ? "The AI will pass priority back to you. Then use the highlighted button to continue." : lesson.steps[step], primary);
  }

  // The player's decks, tutorials, rooms and Play, which opens a new room.
  private renderLanding(): TemplateResult {
    const seatedFirst = (a: RoomSummary, b: RoomSummary) => Number(b.seated) - Number(a.seated);
    const open = this.rooms.filter((r) => !r.started && r.joined < r.max_players).sort(seatedFirst);
    const inProgress = this.rooms.filter((r) => r.started || r.joined >= r.max_players).sort(seatedFirst);
    const head = html`<thead><tr><th>Game</th><th>Players</th><th>Type</th><th>Host</th><th></th></tr></thead>`;
    const cells = (r: RoomSummary) => html`<td><code>${r.game_id}</code></td><td>${r.joined}/${r.max_players}</td><td>${r.game_type}</td><td>${r.host || "—"}</td>`;
    const inGame = this.currentRoom !== null;
    const lockedTitle = "Rejoin or leave your game first";
    return html`
      <div class="home-top">
        ${renderHero({
          decks: this.heroDecks, index: this.heroIndex, show: (i) => this.showHeroDeck(i), deckHref: (id) => this.deckEditorHrefFor(id), learning: this.renderLearningHero(),
        })}
        ${renderTiles({
          openRooms: open.length, liveGames: inProgress.length, decks: this.homeDecks ?? [], heroIndex: Math.max(0, this.heroIndex - 1),
          decksHref: this.deckEditorHref(), jumpTo: (id) => this.renderRoot.querySelector(`#${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }),
        })}
      </div>
      ${this.renderLearning()}
      <div class="lobby rooms">
        ${this.currentRoom ? this.renderCurrentRoom(this.currentRoom) : ""}
        <h3 id="open-rooms">Open rooms <span class="count">(${open.length})</span></h3>
        ${open.length === 0
          ? html`<p class="none">No open rooms yet. Press Play to start one.</p>`
          : html`<table class="room-table">${head}<tbody>
              ${open.map((r) => html`<tr>${cells(r)}
                <td class="room-actions">
                  ${r.seated
                    ? this.renderRejoin(r)
                    : html`<button @click=${() => this.joinRoom(r.game_id)} ?disabled=${inGame} title=${inGame ? lockedTitle : "Take a seat"}>Join</button>
                      <button class="watch" @click=${() => this.watchRoom(r.game_id)} ?disabled=${inGame} title=${inGame ? lockedTitle : "Watch without a seat"}>Watch</button>`}
                </td>
              </tr>`)}
            </tbody></table>`}
        <h3 id="live-games">Live games <span class="count">(${inProgress.length})</span></h3>
        ${inProgress.length === 0
          ? html`<p class="none">No games in progress.</p>`
          : html`<table class="room-table">${head}<tbody>
              ${inProgress.map((r) => html`<tr>${cells(r)}
                <td class="room-actions">
                  ${r.seated
                    ? this.renderRejoin(r)
                    : html`<button class="watch" @click=${() => this.watchRoom(r.game_id)} ?disabled=${inGame} title=${inGame ? lockedTitle : "Watch without a seat"}>Watch</button>`}
                </td>
              </tr>`)}
            </tbody></table>`}
      </div>
      <button class="play" @click=${() => void this.createRoom()} ?disabled=${this.creatingRoom || inGame} title=${inGame ? lockedTitle : "Open a new room"}>Play</button>
    `;
  }

  private renderCurrentRoom(current: CurrentRoom): TemplateResult {
    const leavable = current.release === pageRelease() || pageRelease() === null;
    return html`
      <div class="current-room" role="status">
        <p>You're still in ${current.started ? "a game" : "a room"}, <code>${current.game_id}</code>. Rejoin it, or ${current.started ? "concede" : "leave"} it to play another.</p>
        <div class="room-actions">
          <button @click=${() => this.rejoinCurrentRoom(current)}>Rejoin</button>
          ${leavable ? html`<button class="secondary" @click=${() => void this.leaveCurrentRoom(current)} ?disabled=${this.leavingCurrentRoom}>
            ${this.leavingCurrentRoom ? "Leaving…" : current.started ? "Concede" : "Leave"}</button>` : ""}
        </div>
      </div>
    `;
  }

  private renderProfile(): TemplateResult {
    const own = (this.homeDecks ?? []).filter((d) => !d.starter).length;
    const history: GameHistory = {
      shown: this.gameHistory,
      error: this.gameHistoryError,
      loadingMore: this.loadingMoreHistory,
      loadMore: () => void this.loadGameHistory((this.gameHistory?.page ?? 0) + 1),
      featuredCardOf: (deckId, deck) => this.homeDecks?.find((d) => (deckId === null ? d.name === deck : d.deck_id === deckId))?.featured ?? null,
    };
    const art = bannerCardOf(history, this.homeDecks?.find((d) => d.featured)?.featured ?? null);
    return html`
      <div class="banner profile-banner ${art ? "with-art" : ""}" style=${art ? `--art: url("${artUrl(art)}")` : ""}>
        <span class="profile-face">${(this.displayName || this.user).charAt(0).toUpperCase()}</span>
        <div><h1>${this.displayName}</h1><p>Player id ${this.user}</p>${this.progress ? renderLevel(this.progress) : ""}</div>
        ${renderRecord(history)}
      </div>
      <div class="lobby">
        ${this.progress ? html`<h3>Level ${this.progress.level} <span class="count">(${(this.progress.next_level_xp - this.progress.xp).toLocaleString("en")} XP to level ${this.progress.level + 1})</span></h3>
          ${renderLevelRoad(this.progress, topNavLinks(this.deckEditorHref()).settings)}` : ""}
        <h3>Display name</h3>
        ${this.editingName ? this.renderNameForm() : html`<p>Other players see you as <b>${this.displayName}</b>.
          <button class="link-button" @click=${() => { this.editingName = true; this.nameError = null; }}>Change name</button></p>`}
        <h3>Decks <span class="count">(${own})</span></h3>
        <p>${this.profileDecksLine(own, (this.homeDecks ?? []).length - own)}
          <a href=${this.deckEditorHref()}>Open the deck editor</a></p>
        ${renderGameHistory(history)}
      </div>
    `;
  }

  private profileDecksLine(own: number, starters: number): string {
    if (own === 0) return starters === 0 ? "You haven't built a deck yet." : "You haven't built a deck of your own yet; the starter decks are yours to play or copy.";
    return starters === 0 ? `${own} of your own.` : `${own} of your own, besides the starter decks.`;
  }

  private renderSettings(): TemplateResult {
    return html`
      <div class="banner"><div><h1>Settings</h1><p>Kept in this browser; your unlocks go with your account.</p></div></div>
      <div class="lobby settings">
        <h3>Learn to play</h3>
        <p><button class="secondary" @click=${() => this.replayWalkthrough()}>Replay site walkthrough</button>
          <button class="secondary" @click=${() => { this.heroIndex = 0; this.lessonsOpen = true; this.showPage("home", new URL(topNavLinks(this.deckEditorHref()).home, document.baseURI), location.href); }}>Browse tutorials</button></p>
        <h3>At the table</h3>
        <label class="setting">
          <input type="checkbox" .checked=${this.autoPay} @change=${(e: Event) => this.setAutoPay((e.target as HTMLInputElement).checked)}>
          <span><b>Auto-pay mana costs</b><span class="hint">Pay with the sources the game picks. Off, you choose them each time. The board has the same switch.</span></span>
        </label>
        ${this.renderPreviewSetting()}
        <label class="setting">
          <input type="checkbox" .checked=${this.soundEffects} @change=${(e: Event) => this.setSoundEffects((e.target as HTMLInputElement).checked)}>
          <span><b>Sound effects</b><span class="hint">A click when you press a button, a card's hover and drag, and the table's draws, casts, shuffles and turns.</span></span>
        </label>
        <h3>Unlocks${this.progress ? html` <span class="count">(level ${this.progress.level})</span>` : ""}</h3>
        <label class="setting">
          <input type="checkbox" .checked=${this.showLooks} @change=${(e: Event) => this.setShowLooks((e.target as HTMLInputElement).checked)}>
          <span><b>Show players' looks</b><span class="hint">Card backs, frames, name plates and your battlefield at the table. Off, your table draws every player plain; they still see theirs.</span></span>
        </label>
        ${this.renderCosmeticSetting("card-back", "Card back", "The back of your library, your hand and your face-down cards, which everyone at your games sees.")}
        ${this.renderCosmeticSetting("card-frame", "Card frame", "A frame around your cards, on the battlefield and in your hand, which everyone at your games sees.")}
        ${this.renderCosmeticSetting("battlefield", "Battlefield", "A card's art under the whole table, which only you see.")}
        ${this.renderCosmeticSetting("name-plate", "Name plate", "A plate behind your name at the table, which everyone at your games sees.")}
        <h3>Something wrong?</h3>
        <p><button class="secondary" @click=${() => this.openBugReport()}>Report a bug</button></p>
        <h3>Account</h3>
        ${this.session?.kind === "authkit"
          ? html`<p><button class="secondary" @click=${() => this.signOutEverywhere()}>Sign out</button></p>`
          : html`<p class="none">Signed in with dev auth: change player with ?user= in the address.</p>`}
      </div>
    `;
  }

  private renderCosmeticSetting(cosmetic: Cosmetic, title: string, hint: string): TemplateResult {
    return html`
      <div class="setting cosmetic">
        <span><b>${title}</b><span class="hint">${hint}</span>
          <cosmetic-picker .cosmetic=${cosmetic} .progress=${this.progress}
            .findCardBacks=${() => this.findCardBacks()}
            .choose=${(id: string | null) => this.choose(cosmetic, id)}
            .chosen=${(p: Progress) => { this.progress = p; }}></cosmetic-picker>
        </span>
      </div>
    `;
  }

  private renderPreviewSetting(): TemplateResult {
    return html`
      <div class="setting" role="radiogroup" aria-labelledby="preview-at">
        <span><b id="preview-at">Card preview</b><span class="hint">Where the board shows the card you're pointing at, full size; hold Alt to have its rules text written out too.</span>
          <span class="setting-options">${PREVIEW_PLACES.map((p) => html`<label>
            <input type="radio" name="preview-at" .checked=${this.previewAt === p.at} @change=${() => this.setPreviewAt(p.at)}>${p.label}</label>`)}</span>
        </span>
      </div>
    `;
  }

  private renderRejoin(r: RoomSummary): TemplateResult {
    return html`<button @click=${() => this.joinRoom(r.game_id)} title="Back to your seat">Rejoin</button>`;
  }

  // The server checks the rules (3–24 characters, no control characters).
  private renderNameForm(): TemplateResult {
    return html`
      <form class="name-form" @submit=${(e: SubmitEvent) => void this.saveDisplayName(e)}>
        <label>Display name <input name="display_name" .value=${this.displayName} required autocomplete="nickname"></label>
        <button class="btn" type="submit">Save</button>
        <button class="btn secondary" type="button" @click=${() => { this.editingName = false; this.nameError = null; }}>Cancel</button>
        <span class="hint">What other players see of you.</span>
        ${this.nameError ? html`<div class="error">${this.nameError}</div>` : ""}
      </form>
    `;
  }

  // AuthKit's sign-out; dev auth has none.
  private renderSignOut(): TemplateResult | string {
    if (this.session?.kind !== "authkit") return "";
    return html`<button class="link-button" @click=${() => this.signOutEverywhere()}>Sign out</button>`;
  }

  private debugFacts(): [string, string][] {
    return [
      ["Connection", CONNECTION_LABEL[this.connState] ?? this.connState],
      ["Ping", this.roundTripMs === null ? "measuring…" : `${this.roundTripMs} ms`],
      ["Release", String(pageRelease() ?? "unversioned")],
      ["Seat", this.spectating ? "watching" : this.seat ?? "none"],
      ["Log entries", String(this.history.length)],
      ["Watching", String(this.spectators.length)],
    ];
  }

  private bugDetails(): string {
    return [["Game", this.game] as const, ...this.bugReportFacts, ...this.debugFacts(), ["Browser", navigator.userAgent] as const].map(([k, v]) => `${k}: ${v}`).join("\n");
  }

  private async submitBugReport(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (this.bugReportSending) return;
    this.bugReportSending = true;
    this.bugReportError = null;
    try {
      const response = await this.api("bug-reports", {
        method: "POST", headers: { "Content-Type": "application/json" }, signal: AbortSignal.timeout(30000),
        body: JSON.stringify({ title: this.bugReportTitle, happened: this.bugReportHappened,
          expected: this.bugReportExpected, details: this.bugDetails(), game_id: this.game }),
      });
      if (!response.ok) throw new Error(await response.text() || `Could not send report (HTTP ${response.status}).`);
      this.bugReportSent = true;
      this.bugReportTitle = this.bugReportHappened = this.bugReportExpected = "";
    } catch (err) {
      this.bugReportError = err instanceof Error && err.name !== "TimeoutError" && !(err instanceof TypeError)
        ? err.message : "Could not confirm delivery. Your text is still here; please wait before retrying.";
    } finally { this.bugReportSending = false; }
  }

  private onReportIssue = (e: Event): void => {
    this.openBugReport((e as CustomEvent<ReportIssueDetail>).detail.facts);
  };

  private openBugReport(facts: [string, string][] = []): void {
    this.bugReportFacts = facts;
    this.bugReportSent = false;
    this.bugReportError = null;
    this.bugReportOpen = true;
    void this.updateComplete.then(() => this.shadowRoot?.querySelector<HTMLDialogElement>("dialog.bug-report-dialog")?.showModal());
  }

  private closeBugReport(): void {
    this.shadowRoot?.querySelector<HTMLDialogElement>("dialog.bug-report-dialog")?.close();
  }

  private renderBugReport(): TemplateResult {
    return html`
      <dialog class="bug-report-dialog" aria-labelledby="bug-report-title" @close=${() => { this.bugReportOpen = false; }}
        @click=${(e: Event) => { if (e.target === e.currentTarget && !this.bugReportSending) this.closeBugReport(); }}>
        <div class="sheet">${!this.bugReportOpen ? "" : this.bugReportSent ? html`
          <h3 id="bug-report-title">Report sent</h3>
          <p role="status">Thanks — your bug report has been sent.</p>
          <div class="row-actions"><button class="btn secondary" autofocus @click=${() => this.closeBugReport()}>Close</button></div>` : html`
          <form class="bug-report" @submit=${(e: SubmitEvent) => void this.submitBugReport(e)}>
            <h3 id="bug-report-title">Report an issue</h3>
            <p class="hint">Sent privately to the developers. Your account ID and the diagnostic details below are included. Don't include passwords or other sensitive information.</p>
            <label>Title<input autofocus ?disabled=${this.bugReportSending} required minlength="5" maxlength="120" .value=${this.bugReportTitle}
              @input=${(e: Event) => { this.bugReportTitle = (e.target as HTMLInputElement).value; }}></label>
            <label>What happened?<textarea ?disabled=${this.bugReportSending} required minlength="10" maxlength="5000" rows="4" .value=${this.bugReportHappened}
              @input=${(e: Event) => { this.bugReportHappened = (e.target as HTMLTextAreaElement).value; }}></textarea></label>
            <label>What did you expect?<textarea ?disabled=${this.bugReportSending} required minlength="3" maxlength="3000" rows="3" .value=${this.bugReportExpected}
              @input=${(e: Event) => { this.bugReportExpected = (e.target as HTMLTextAreaElement).value; }}></textarea></label>
            <details><summary>Diagnostic details</summary><pre>${this.bugDetails()}</pre></details>
            <p class="hint">One report per minute, up to ten per day.</p>
            ${this.bugReportError ? html`<p class="error" role="alert">${this.bugReportError}</p>` : ""}
            <div class="row-actions">
              <button class="btn" type="submit" ?disabled=${this.bugReportSending}>${this.bugReportSending ? "Sending…" : "Send bug report"}</button>
              <button class="btn secondary" type="button" ?disabled=${this.bugReportSending} @click=${() => this.closeBugReport()}>Cancel</button>
            </div>
          </form>`}</div>
      </dialog>
    `;
  }

  private async copyBugDetails(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.bugDetails());
      this.bugDetailsCopied = true;
      window.setTimeout(() => { this.bugDetailsCopied = false; }, 2000);
    } catch { /* the details still show in the menu */ }
  }

  private renderBoardMenu(): TemplateResult {
    return html`<div slot="menu" class="board-menu">
      <section>
        <h3>Settings</h3>
        ${this.renderPreviewSetting()}
      </section>
      <section>
        <h3>Connection</h3>
        <dl>${this.debugFacts().map(([k, v]) => html`<dt>${k}</dt><dd>${v}</dd>`)}</dl>
        <button class="link-button" @click=${() => this.wire?.ping()}>Ping again</button>
      </section>
      <section>
        <h3>Something wrong?</h3>
        <button class="link-button" @click=${() => this.openBugReport()}>Report a bug</button>
        <button class="link-button" @click=${() => void this.copyBugDetails()}>${this.bugDetailsCopied ? "Copied" : "Copy details"}</button>
      </section>
      ${this.session?.kind === "authkit" ? html`<section>${this.renderSignOut()}</section>` : ""}
    </div>`;
  }

  // A visitor whose sign-in didn't take, or whose session ended, sees only "Sign in": no lobby
  // list, no rooms.
  private renderSignIn(): TemplateResult {
    const asUser = (user: string): string => this.game ? `?game=${encodeURIComponent(this.game)}&user=${user}` : `?user=${user}`;
    const devHint = this.authMode === "dev" ? html`
      <p>Dev mode — auth is disabled. Pass <code>?user=&lt;name&gt;</code> in the URL.</p>
      <p>Try <a href=${asUser("alice")}>${asUser("alice")}</a>${this.game ? "" : " to create a room,"}
         and <a href=${asUser("bob")}>${asUser("bob")}</a> in a second tab${this.game ? "" : " to join it"}.</p>
    ` : "";
    const error = this.signInError ? html`<div class="error">${this.signInError}</div>` : "";
    if (this.authkit) {
      return html`
        <div class="lobby landing">
          <h1><img src="brand/logo.png" alt="On the Stack" width="400" height="103" /></h1>
          ${error}
          <button class="sign-in" @click=${() => this.signInWithAuthKit()}>Sign in</button>
          ${devHint}
        </div>
      `;
    }
    if (devHint) return html`<div class="lobby">${devHint}</div>`;
    return html`<div class="lobby"><p>Sign-in isn't set up here yet.</p></div>`;
  }

  private renderLobby(): TemplateResult {
    if (this.spectating) return this.renderWatchedLobby();
    const myDeck = this.myPickedDeck();
    const me = this.myLobby();
    const meReady = me?.ready ?? false;
    const isHost = me?.is_host ?? false;
    const canReady = myDeck !== null || this.scenario !== null;
    // A deck picked before the Host switched the game type stays picked, but isn't legal here.
    const mismatched = this.availableDecks?.find((d) => d.name === myDeck && d.format !== this.gameType);
    const host = this.lobby.find((u) => u.is_host)?.display_name;
    return html`
      <div class="banner">
        <div>
          <h1>${host ? `${host}'s room` : "Room"}</h1>
          <p><span class="tag">${this.gameType}</span><span class="tag">${this.strict ? "Strict" : "Casual"}</span>
            ${this.scenario !== null ? html`<span class="tag" title="The game starts from this board, without the pregame; no deck is needed">Scenario</span>` : ""}
            <span>${this.lobby.length} of ${this.maxPlayers} seats taken</span></p>
        </div>
      </div>
      <div class="lobby">
        <p>${this.lobbyMessage || "Waiting…"}</p>
        ${this.renderSeatGrid(isHost, meReady, false, this.renderMatchSettings(isHost, meReady))}
        ${mismatched ? html`
          <div class="lobby-row deck-warnings">
            <span class="lobby-label">${this.gameType}:</span>
            <span class="hint">${myDeck} is a ${mismatched.format} deck, so a game against AI seats alone earns no XP with it. Pick a ${this.gameType} deck.</span>
          </div>`
        : this.deckWarnings.length > 0 ? html`
          <div class="lobby-row deck-warnings">
            <span class="lobby-label">Casual:</span>
            <span class="hint">${myDeck ?? "This deck"} plays here, but ${this.deckWarnings.join("; ")}. A game against AI seats alone earns no XP with it.</span>
          </div>` : ""}

        ${this.spectators.length > 0
          ? html`<div class="lobby-row"><span class="lobby-label">Watching:</span>
              <span>${this.spectators.map((s) => s.display_name).join(", ")}</span></div>`
          : ""}

        <div class="lobby-row actions">
          ${meReady
            ? html`<button class="secondary" @click=${() => this.wire?.unready()}
                      title="Take back your Ready to change your deck or the table's settings">
                Unready
              </button>`
            : html`<button @click=${() => this.sendReady()} ?disabled=${!canReady}>Ready</button>`}
          <button class="secondary" @click=${() => this.leaveRoom()}
                  title="Leave this room (pre-game only)">
            Leave
          </button>
          ${isHost ? this.othersInLobby().filter((opp) => opp.ai.kind !== "Some").map((opp) => html`
            <button class="danger" @click=${() => this.kickUser(opp.user_id)}
                    title="Remove ${opp.display_name} from the room">
              Kick ${opp.display_name}
            </button>`) : ""}
        </div>
      </div>
    `;
  }

  private renderMatchSettings(isHost: boolean, meReady: boolean, watched = false): TemplateResult {
    const locked = meReady || !isHost;
    const why = isHost ? "Unready to change the table's settings" : "The host sets the table's settings";
    const clock = this.clockSetting;
    return html`
      <div class="match-settings" role="group" aria-label="Match settings">
        <div class="match-setting">
          <span class="match-setting-label" id="format-label">Format</span>
          <div class="segmented" role="group" aria-labelledby="format-label" title=${locked ? why : "How much life each player starts with"}>
            ${GAME_TYPES.map((type) => html`
              <button class=${this.gameType === type.kind ? "on" : ""} aria-pressed=${this.gameType === type.kind ? "true" : "false"}
                ?disabled=${locked} @click=${() => this.wire?.setGameType(type.kind)}>${type.kind} <small>${type.life} life</small></button>`)}
          </div>
        </div>
        <div class="match-setting">
          <span class="match-setting-label" id="deck-rules-label">Deck rules</span>
          <div class="segmented" role="group" aria-labelledby="deck-rules-label">
            <button class=${this.strict ? "" : "on"} aria-pressed=${this.strict ? "false" : "true"} ?disabled=${locked}
              title=${locked ? why : "A deck that breaks its format's rules still plays, with a warning"}
              @click=${() => this.wire?.setStrict(false)}>Casual</button>
            <button class=${this.strict ? "on" : ""} aria-pressed=${this.strict ? "true" : "false"} ?disabled=${locked}
              title=${locked ? why : "A deck that breaks its format's rules can't play"}
              @click=${() => this.wire?.setStrict(true)}>Strict</button>
          </div>
        </div>
        <div class="match-setting">
          <span class="match-setting-label" id="mulligans-label">Mulligans</span>
          <div class="segmented" role="group" aria-labelledby="mulligans-label">
            ${MULLIGAN_RULES.map((rule) => html`
              <button class=${this.mulligans === rule.kind ? "on" : ""} aria-pressed=${this.mulligans === rule.kind ? "true" : "false"}
                ?disabled=${locked} title=${locked ? why : rule.title}
                @click=${() => this.wire?.setMulligans(rule.kind)}>${rule.label}</button>`)}
          </div>
        </div>
        ${watched ? "" : html`
          <div class="match-setting">
            <span class="match-setting-label" id="clock-label">Clock</span>
            <div class="segmented" role="group" aria-labelledby="clock-label" title=${locked ? why : "Each player's chess clock for the whole game"}>
              ${CLOCK_LENGTHS.map((length) => {
                const on = clockLengthsMatch(clock.length, length);
                return html`<button class=${on ? "on" : ""} aria-pressed=${on ? "true" : "false"} ?disabled=${locked}
                  @click=${() => this.wire?.setClock({ length, increment_seconds: length.kind === "NoClock" ? 0 : clock.increment_seconds })}>${this.clockLengthLabel(length)}</button>`;
              })}
            </div>
            ${clock.length.kind === "NoClock" ? "" : html`
              <div class="segmented" role="group" aria-label="Time added each turn" title=${locked ? why : "Time added to a player's clock at the start of each of their turns"}>
                ${CLOCK_INCREMENTS_SECONDS.map((seconds) => html`
                  <button class=${clock.increment_seconds === seconds ? "on" : ""} aria-pressed=${clock.increment_seconds === seconds ? "true" : "false"} ?disabled=${locked}
                    @click=${() => this.wire?.setClock({ length: clock.length, increment_seconds: seconds })}>+${seconds}s</button>`)}
              </div>`}
          </div>`}
        ${watched ? "" : isHost
          ? html`<details class="advanced-settings">
              <summary>More options</summary>
              <div class="advanced-settings-content">
                ${this.renderExhibitionToggle(meReady)}
                ${this.authMode === "dev" ? this.renderScenarioBox(meReady) : ""}
                ${this.watchSpeed
                  ? html`<div class="lobby-row"><span class="lobby-label">AI speed:</span>${this.renderWatchSpeedControls()}
                      <span class="hint">(how fast you, and anyone watching, see it play)</span></div>`
                  : ""}
              </div>
            </details>`
          : html`<span class="match-settings-note">The host sets these</span>`}
      </div>
    `;
  }

  private clockLengthLabel(length: ClockLength): TemplateResult | string {
    switch (length.kind) {
      case "FormatClock": return html`Format <small>${FORMAT_CLOCK_MINUTES[this.gameType]} min</small>`;
      case "ClockMinutes": return `${length.values[0]} min`;
      case "NoClock": return "Off";
    }
  }

  private renderSeatGrid(isHost: boolean, meReady: boolean, watched = false, settings: TemplateResult | "" = ""): TemplateResult {
    const canEdit = isHost && (watched || !meReady);
    const count = Math.max(this.maxPlayers, this.lobby.length);
    const seats = Array.from({ length: count }, (_, index) => this.lobby[index]);
    const minimum = Math.max(2, this.lobby.length);
    const maxSize = this.exhibition ? EXHIBITION_SIZES[EXHIBITION_SIZES.length - 1] : ROOM_SIZES[ROOM_SIZES.length - 1];
    return html`
      <section class="seat-lobby" aria-labelledby="player-seats-title">
        <header class="seat-lobby-heading">
          <div>
            <h2 id="player-seats-title">Players</h2>
            <p>${this.lobby.length} of ${this.maxPlayers} seats filled</p>
          </div>
          ${isHost
            ? html`<div class="seat-stepper" role="group" aria-label="Room seat count">
                <button class="secondary" aria-label="Remove a seat" title="Remove a seat"
                  ?disabled=${!canEdit || this.maxPlayers <= minimum}
                  @click=${() => this.adjustMaxPlayers(-1)}>−</button>
                <span class="seat-stepper-value">${this.maxPlayers}<small>seats</small></span>
                <button aria-label="Add a seat" title="Add a seat"
                  ?disabled=${!canEdit || this.maxPlayers >= maxSize}
                  @click=${() => this.adjustMaxPlayers(1)}>+</button>
              </div>`
            : html`<span class="hint">${this.maxPlayers} seats at this table</span>`}
        </header>
        ${settings}
        <div class="player-slots">
          ${seats.map((player, index) => {
            if (!player) {
              const canAddAi = canEdit && index === this.lobby.length;
              const adding = canAddAi && this.aiSlotOpen === index;
              const aiChoice = this.availableDecks?.find((d) => d.deck_id === this.aiDeck);
              return adding
                ? html`<div class="player-slot empty configuring" aria-label=${`Configure AI player for seat ${index + 1}`}>
                    <span class="empty-slot-plus" aria-hidden="true">+</span>
                    <div class="empty-slot-copy">
                      <span class="player-slot-number">Seat ${index + 1}</span>
                      <b>Configure AI opponent</b>
                      <span>Choose a deck and difficulty</span>
                    </div>
                    <div class="slot-ai-form">
                      <select aria-label="AI deck" class=${aiChoice && !this.deckLegal(aiChoice) ? "deck-illegal" : ""}
                        @change=${(e: Event) => { this.aiDeck = (e.target as HTMLSelectElement).value; }}>
                        <option value="" ?selected=${this.aiDeck === ""}>Choose deck</option>
                        ${this.deckOptions((d) => this.aiDeck === d.deck_id)}
                      </select>
                      <select aria-label="AI difficulty" @change=${(e: Event) => { this.aiLevel = AI_LEVELS.find((level) => level === (e.target as HTMLSelectElement).value) ?? "Medium"; }}>
                        ${AI_LEVELS.map((level) => html`<option value=${level} ?selected=${this.aiLevel === level}>${level}</option>`)}
                      </select>
                      <div class="slot-ai-actions">
                        <button @click=${() => this.addAiSeat()} ?disabled=${this.aiDeck === "" || (aiChoice !== undefined && this.deckRefusal(aiChoice) !== null) || this.lobby.length >= this.maxPlayers}>Add player</button>
                        <button class="secondary" @click=${() => { this.aiSlotOpen = null; }}>Cancel</button>
                      </div>
                    </div>
                  </div>`
                : canAddAi
                  ? html`<button class="player-slot empty" aria-label=${`Add an AI opponent in seat ${index + 1}`}
                      @click=${() => { this.aiSlotOpen = index; }}>
                      <span class="empty-slot-plus" aria-hidden="true">+</span>
                      <span class="empty-slot-copy">
                        <span class="player-slot-number">Seat ${index + 1}</span>
                        <b>Add opponent</b>
                        <span>Set up an AI player</span>
                      </span>
                    </button>`
                  : html`<div class="player-slot empty" aria-label=${`Open seat ${index + 1}`}>
                      <span class="empty-slot-plus waiting" aria-hidden="true">·</span>
                      <span class="empty-slot-copy">
                        <span class="player-slot-number">Seat ${index + 1}</span>
                        <b>Open seat</b>
                        <span>Waiting for a player</span>
                      </span>
                    </div>`;
            }

            const isYou = player.user_id === this.user && !watched;
            const deckName = player.deck.kind === "Some" ? player.deck.values[0] : null;
            const chosen = this.availableDecks?.find((d) => d.name === deckName);
            const aiLevel = player.ai.kind === "Some" ? player.ai.values[0].kind : null;
            const status = player.ready ? "Ready" : aiLevel ? `AI · ${aiLevel}` : deckName ? "Deck selected" : "Choosing a deck";
            return html`<article class="player-slot ${isYou ? "you" : ""}">
              <span class="player-slot-portrait" aria-hidden="true"><img src=${playerPortraitAt(index)} alt="" draggable="false"></span>
              <div class="player-slot-person">
                <div class="player-slot-person-copy">
                  <span class="player-slot-number">Seat ${index + 1}</span>
                  <div class="player-slot-name-line">
                    <b>${player.display_name}</b>
                    ${player.is_host ? html`<span class="host-badge">Host</span>` : ""}
                    ${aiLevel ? html`<span class="ai-badge">AI · ${aiLevel}</span>` : ""}
                  </div>
                  <span>${aiLevel ? "Computer opponent" : isYou ? "You" : player.is_host ? "Room host" : "Player"}</span>
                </div>
              </div>
              <div class="player-slot-details">
                <span class="player-slot-deck-label">Deck</span>
                ${isYou
                ? html`<div class="player-slot-deck">
                    ${this.availableDecks === null
                      ? html`<span class="none">Loading decks…</span>`
                      : this.availableDecks.length === 0
                      ? html`<span class="none">No decks yet</span>`
                      : html`<select aria-label="Your deck" class=${chosen && !this.deckLegal(chosen) ? "deck-illegal" : ""}
                          @change=${(e: Event) => this.pickDeck((e.target as HTMLSelectElement).value)}>
                          <option value="" ?selected=${deckName === null}>Choose your deck</option>
                          ${this.deckOptions((d) => deckName === d.name)}
                        </select>`}
                    <a class="deck-editor-link" href=${this.deckEditorHref()}>Edit decks</a>
                  </div>`
                : html`<div class="player-slot-deck"><span class="opp-deck">${deckName ?? "No deck selected"}</span></div>`}
                <span class="player-slot-state ${player.ready ? "ready" : ""}">${status}</span>
              </div>
              ${canEdit && aiLevel
                ? html`<button class="slot-remove" aria-label=${`Remove AI opponent ${player.display_name}`} title="Remove this AI seat"
                    @click=${() => this.removeAiSeat(player.user_id)}>−</button>`
                : ""}
            </article>`;
          })}
        </div>
      </section>
    `;
  }

  // Only AI seats play an exhibition; the Host stands up and watches.
  // Dev auth only: a .scenario board (mtg/scenarios) the game starts from. The users who join take
  // its PLAYER lines in order, the Host first.
  private renderScenarioBox(disabled: boolean): TemplateResult {
    return html`
      <div class="lobby-row scenario-box">
        <label for="scenario-text" class="lobby-label">Scenario <span class="hint">(dev)</span></label>
        <textarea id="scenario-text" rows="6" spellcheck="false" ?disabled=${disabled}
          placeholder=${"PLAYER p1 20 Alice\nPLAYER p2 20 Bob\np1 battlefield Kíli the Resourceful\np1 hand Chainsaw"}
          .value=${this.scenarioDraft} @input=${(e: Event) => { this.scenarioDraft = (e.target as HTMLTextAreaElement).value; }}></textarea>
        <button class="secondary" ?disabled=${disabled || this.scenarioDraft.trim() === ""}
          @click=${() => this.wire?.setScenario(this.scenarioDraft)}>Load scenario</button>
        ${this.scenario !== null ? html`<button class="secondary" ?disabled=${disabled} @click=${() => this.wire?.setScenario("")}>Clear scenario</button>` : ""}
      </div>
    `;
  }

  private renderExhibitionToggle(disabled: boolean): TemplateResult {
    return html`
      <div class="lobby-row">
        <label title="Only AI seats play, at a pace you choose, and you watch">
          <input type="checkbox" ?checked=${this.exhibition} ?disabled=${disabled}
            @change=${(e: Event) => this.wire?.setExhibition((e.target as HTMLInputElement).checked)}>
          Exhibition <span class="hint">(only AI seats play; you watch)</span>
        </label>
      </div>
    `;
  }

  private isSpectatingHost(): boolean {
    return this.spectators.some((s) => s.user_id === this.user && s.is_host);
  }

  // A spectator's lobby: who will play, and who watches. An exhibition's Host fills its seats here.
  private renderWatchedLobby(): TemplateResult {
    const isHost = this.isSpectatingHost();
    const hostBadge = html`<span class="host-badge" title="Room host">Host</span>`;
    const seatsLeft = this.maxPlayers - this.lobby.length;
    const message = !this.exhibition ? "You're watching. The game starts once its players are Ready."
      : isHost ? `Exhibition: AI seats play while you watch. It starts once every seat has an AI (${seatsLeft} to go).`
      : "You're watching an exhibition: AI seats play it.";
    return html`
      <div class="lobby watching">
        <p>${message}</p>
        ${this.renderSeatGrid(isHost, false, true, this.renderMatchSettings(isHost, false, true))}
        <div class="lobby-row">
          <span class="lobby-label">Watching:</span>
          <span>${this.spectators.map((s) => s.display_name).join(", ") || "—"}</span>
          ${isHost ? hostBadge : ""}
        </div>
        ${isHost ? html`
          ${this.renderExhibitionToggle(false)}` : ""}
        ${this.watchSpeed ? html`<div class="lobby-row"><span class="lobby-label">AI speed:</span>${this.renderWatchSpeedControls()}</div>` : ""}
        <div class="lobby-row actions">
          <button class="secondary" @click=${() => this.goToRoomBrowser()}>Stop watching</button>
        </div>
      </div>
    `;
  }

  // 0.5× to 4×, or Instant, and pause and step: how fast AI seats answer while someone watches, and
  // how long each of their actions stays on the board (./board/paced-updates).
  private renderWatchSpeedControls(): TemplateResult | "" {
    const current = this.watchSpeed;
    if (!current) return "";
    const locked = !this.mayChangeWatchSpeed;
    return html`
      <div class="watch-speed" role="group" aria-label="AI speed" title=${locked ? "Only the host sets how fast the AI seats play" : "How fast the AI seats play"}>
        ${SPEEDS.map((s) => html`
          <button class="speed-btn ${current.speed.kind === s.kind ? "on" : ""}" aria-pressed=${current.speed.kind === s.kind ? "true" : "false"}
            ?disabled=${locked} @click=${() => this.wire?.setSpeed(s.kind)}>${s.label}</button>`)}
        <button class="speed-btn" ?disabled=${locked} @click=${() => this.wire?.setPaused(!current.paused)}>${current.paused ? "Resume" : "Pause"}</button>
        <button class="speed-btn" ?disabled=${locked || !current.paused} @click=${() => this.wire?.stepAi()}
          title="The AI seat the game waits on answers once">Step</button>
      </div>
    `;
  }


  // Leaving a room before its game starts frees the seat; leaving a started game concedes it, unless
  // another player has dropped: then it doesn't count. Either way this page goes home once the Room
  // has let the player go, or after a moment if it says nothing. A player already out goes home at once.
  private leaveRoom(): void {
    if (this.isOutOfGame()) {
      this.leaving = true;
      this.wire?.leaveRoom();
      this.goToRoomBrowser();
      return;
    }
    const dropped = this.droppedNames();
    const uncounted = this.gameState !== null && dropped.length > 0;
    const question = uncounted
      ? `${dropped.join(", ")} ${dropped.length === 1 ? "has" : "have"} been gone 5 minutes. Leave without it counting? Once everyone still here has left, the game is cancelled.`
      : this.gameState ? "Leave this game? You concede it." : "Leave this room?";
    if (!confirm(question)) return;
    this.leaving = true;
    if (uncounted) this.wire?.leaveUncounted(); else this.wire?.leaveRoom();
    window.setTimeout(() => this.goToRoomBrowser(), 1500);
  }

  private noteDrops(): void {
    if (this.dropTimer !== null) window.clearTimeout(this.dropTimer);
    this.dropTimer = null;
    const now = Date.now();
    this.droppedNow = [...this.dropsAt].filter(([user, at]) => at <= now && user !== this.user).map(([user]) => user);
    const next = Math.min(...[...this.dropsAt.values()].filter((at) => at > now));
    if (Number.isFinite(next)) this.dropTimer = window.setTimeout(() => this.noteDrops(), next - now);
  }

  private droppedNames(): string[] {
    return this.droppedNow.map((id) => this.lobby.find((u) => u.user_id === id)?.display_name ?? id);
  }

  private renderDropped(): TemplateResult | string {
    const dropped = this.droppedNames();
    if (!this.gameState || this.ended || this.spectating || dropped.length === 0) return "";
    return html`<span class="dropped-note" role="status">${dropped.join(", ")} dropped: Leave won't count against you.</span>`;
  }

  private kickUser(target: string): void {
    if (!confirm(`Kick ${target} from this room?`)) return;
    this.wire?.kickUser(target);
  }

  private renderEnded(state: PublicState): TemplateResult {
    return renderGameOver({
      state,
      earned: this.progressEarned ? renderXpEarned(this.progressEarned.before, this.progressEarned.after) : undefined, history: this.history, winner: this.winner, seat: this.spectating ? null : this.seat,
      deckOf: (seat) => {
        const user = this.pregame.find((p) => p.seat === seat)?.user_id;
        const deck = this.lobby.find((u) => u.user_id === user)?.deck;
        return deck?.kind === "Some" ? deck.values[0] : null;
      },
      mulligansOf: (seat) => this.pregame.find((p) => p.seat === seat)?.mulligans ?? 0,
      backToRooms: () => this.goToRoomBrowser(),
      review: html`<match-review .frames=${this.replayFrames} .total=${this.replayTotal} .loading=${this.replayLoading}
        .received=${this.replayReceived} .size=${this.replaySize}
        .error=${this.replayError} .history=${this.history} .finalState=${state} .seat=${this.seat ?? ""}
        .game=${this.game} .pregame=${this.pregame} .startingLife=${this.startingLife()}
        .reload=${() => this.loadReplay()}></match-review>`,
    });
  }

  private loadReplay(): void {
    if (this.connState === "archived") {
      void this.reviewArchived();
      return;
    }
    this.replayFrames = [];
    this.replayTotal = 0;
    this.replayError = null;
    this.replayLoading = true;
    void this.reviewStored();
  }

  private async reviewMessagesCountingBytes(r: Response): Promise<ServerMsg[]> {
    this.replayReceived = 0;
    this.replaySize = Number(r.headers.get("Content-Length")) || 0;
    if (!r.body) return await r.json() as ServerMsg[];
    const reader = r.body.getReader();
    const chunks: Uint8Array[] = [];
    for (let read = await reader.read(); !read.done; read = await reader.read()) {
      chunks.push(read.value);
      this.replayReceived += read.value.byteLength;
    }
    const bytes = new Uint8Array(this.replayReceived);
    let at = 0;
    for (const chunk of chunks) { bytes.set(chunk, at); at += chunk.byteLength; }
    return JSON.parse(new TextDecoder().decode(bytes)) as ServerMsg[];
  }

  private async reviewStored(): Promise<void> {
    let messages: ServerMsg[] | null = null;
    try {
      const r = await this.api(`games/${encodeURIComponent(this.game)}/review`);
      if (r.ok) messages = await this.reviewMessagesCountingBytes(r);
    } catch { /* the room replays it instead */ }
    if (messages === null) {
      this.wire?.send({ kind: "GetReplay", values: [] });
      return;
    }
    for (const msg of messages) if (msg.kind === "ReplayUpdate") this.onMessage(msg);
  }

  // A game its room has forgotten: once finished, the release that played it replays its saved log
  // into the messages reconnecting and GetReplay would have sent. Anything else goes back to the rooms.
  private async reviewArchived(): Promise<void> {
    let messages: ServerMsg[];
    try {
      const r = await this.api(`games/${encodeURIComponent(this.game)}/review`);
      if (!r.ok) throw new Error(`${r.status}`);
      messages = await this.reviewMessagesCountingBytes(r);
    } catch {
      if (this.connState === "archived") {
        this.replayLoading = false;
        this.replayError = "Couldn't load the saved game. Try loading it again.";
      } else this.goToRoomBrowser();
      return;
    }
    this.connState = "archived";
    this.replayFrames = [];
    this.replayTotal = 0;
    this.replayError = null;
    this.replayLoading = true;
    for (const msg of messages) this.onMessage(msg);
    if (this.replayLoading || this.replayFrames.length === 0) {
      this.replayLoading = false;
      this.replayError ??= "The saved game has nothing to replay.";
    }
  }

  // The AI seats' levels, by seat.
  private aiLevels(): { [seat: string]: string } {
    return Object.fromEntries(this.pregame.flatMap((p) => {
      const ai = this.lobby.find((u) => u.user_id === p.user_id)?.ai;
      return ai?.kind === "Some" ? [[p.seat, ai.values[0].kind]] : [];
    }));
  }

  // 103.4: each player starts with 20 life, or 40 in Commander (903.7).
  private startingLife(): number {
    return this.gameType === "Commander" ? 40 : 20;
  }

  private renderMatchIntro(): TemplateResult {
    const players = this.pregame.length > 0
      ? this.pregame.map((seat) => {
          const user = this.lobby.find((candidate) => candidate.user_id === seat.user_id);
          return {
            user_id: seat.user_id,
            display_name: seat.display_name,
            seat: seat.seat,
            deck: user?.deck.kind === "Some" ? user.deck.values[0] : null,
            ai: user?.ai.kind === "Some" ? user.ai.values[0].kind : null,
          };
        })
      : this.lobby.map((user) => ({
          user_id: user.user_id,
          display_name: user.display_name,
          seat: null,
          deck: user.deck.kind === "Some" ? user.deck.values[0] : null,
          ai: user.ai.kind === "Some" ? user.ai.values[0].kind : null,
        }));
    const avatarIndexOf = (player: (typeof players)[number], index: number) => {
      const turnPosition = player.seat && this.gameState ? this.gameState.turn_order.indexOf(player.seat) : index;
      return turnPosition < 0 ? index : turnPosition;
    };
    const renderSide = (player: (typeof players)[number], index: number, side: string, order: number) => {
      const avatar = avatarIndexOf(player, index);
      return html`
      <article class="vs-side ${side}" style=${`--glow: ${playerGlowAt(avatar)}; --i: ${order}`}>
        <img class="vs-avatar" src=${playerPortraitAt(avatar)} alt="">
        <div class="vs-plate">
          <b>${player.display_name}</b>
          <span>${player.deck || (player.ai ? "Ready to duel" : "Choosing a deck")}</span>
        </div>
      </article>`;
    };
    const yours = players.findIndex((player) => player.user_id === this.user);
    const [left, right] = yours === 1 ? [1, 0] : [0, 1];
    const format = `${this.gameType}, ${this.startingLife()} life`;
    return html`
      <main class="match-intro" aria-label="Loading match" style=${`--intro-ms: ${MATCH_INTRO_MS}ms`}>
        ${players.length === 2
          ? html`
            ${renderSide(players[right], right, "right", 1)}
            ${renderSide(players[left], left, "left", 0)}
            <svg class="vs-seam" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              <line class="split" x1="57" y1="0" x2="45" y2="100" pathLength="1" vector-effect="non-scaling-stroke"></line>
              <line class="stacked" x1="0" y1="54" x2="100" y2="46" pathLength="1" vector-effect="non-scaling-stroke"></line>
            </svg>
            <h1 class="vs-mark"><span class="sr-only">${players[left].display_name} versus ${players[right].display_name}</span><span aria-hidden="true">VS</span></h1>`
          : players.length > 0
            ? html`<div class="vs-table">${players.map((player, index) => renderSide(player, index, "", index))}</div>`
            : html`<div class="vs-empty"><b>Preparing the table</b></div>`}
        <p class="vs-format">${format}</p>
        <div class="vs-status" role="status">Shuffling up</div>
      </main>
    `;
  }

  private renderBoard(state: PublicState): TemplateResult {
    const view: BoardView = {
      game: this.game,
      seat: this.seat ?? "",
      state,
      pacing: this.pacing,
      history: this.history,
      moves: this.moves,
      error: this.lastError,
      autoPay: this.autoPay,
      autoPaying: this.autoPaying,
      aiLevels: this.aiLevels(),
      catchingUp: this.catchingUp,
      walking: this.walking,
      turnBannerMs: this.spectating ? 0 : turnBeatMs(this.speed()),
      startingLife: this.startingLife(),
      undoable: this.undoable,
      clocks: this.clocks,
      pregame: this.pregame,
      previewAt: this.previewAt,
      looks: this.looks,
      seatLooks: this.showLooks ? this.seatLooks : {},
      battlefield: this.showLooks ? this.progress?.battlefields.find((b) => b.id === this.progress?.battlefield) ?? null : null,
    };
    // A spectator's board is read-only: the same table, with no intents to send.
    return html`
      ${this.renderTutorialCoach(state)}
      ${this.renderWatchBar()}
      <arena-board .view=${view} .intents=${this.spectating ? null : this.intents}><span slot="meta">${this.renderDropped()}</span>${this.renderBoardMenu()}</arena-board>
      ${this.renderRotateHint()}
    `;
  }

  // The table needs a phone on its side: upright, it covers the board until the phone turns. Where the
  // browser allows it, full screen can turn it for you.
  private renderRotateHint(): TemplateResult {
    const turn = async (): Promise<void> => {
      try {
        await document.documentElement.requestFullscreen({ navigationUI: "hide" });
        await (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> }).lock?.("landscape");
      } catch { /* Not every browser goes full screen or turns; turning the phone still works. */ }
    };
    return html`<div class="rotate-hint" role="alert">
      <svg viewBox="0 0 48 48" width="64" height="64" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <rect x="15" y="6" width="18" height="30" rx="3"></rect><path d="M8 40a18 18 0 0 0 32 0"></path><path d="M36 36l4 4 2-5"></path>
      </svg>
      <b>Turn your phone sideways</b>
      <span>The table needs landscape to fit your battlefield, hand and the stack.</span>
      ${document.fullscreenEnabled ? html`<button class="btn" @click=${turn}>Go full screen</button>` : ""}
    </div>`;
  }

  // Above the table while anyone watches: a spectator's way out, and the AI speed for whoever may set it.
  private renderWatchBar(): TemplateResult | "" {
    const speed = this.spectating || (this.mayChangeWatchSpeed && this.spectators.length > 0) ? this.renderWatchSpeedControls() : "";
    if (!this.spectating && speed === "") return "";
    return html`<div class="watch-bar">
      ${this.spectating ? html`<span>Watching <b>${this.game}</b></span>` : html`<span>${this.spectators.length} watching</span>`}
      ${speed ? html`<span class="lobby-label">AI speed:</span>${speed}` : ""}
      ${this.spectating ? html`<button class="btn" @click=${() => this.goToRoomBrowser()}>Stop watching</button>` : ""}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "spell-mtg": SpellMtg;
  }
}

// Whether `credential` signs this page in, and if nothing does, how it signs in. A refusal
// throws with the server's reason.
// The client bundles every mtg/scenarios/*.scenario as text, so a name loads one, and anything
// with a PLAYER line is a scenario already.
async function scenarioText(nameOrText: string): Promise<string> {
  if (/^\s*PLAYER\s/m.test(nameOrText)) return nameOrText;
  const bundled = await import(`../../scenarios/${nameOrText}.scenario`) as { default: string };
  return bundled.default;
}

async function fetchMe(credential: Credential | null): Promise<MeResponse> {
  const [url, init] = credential ? authorizedRequest("me", credential) : ["me", {}];
  const response = await fetch(url, init);
  if (!response.ok) throw new Error((await response.text()) || `/me answered ${response.status}`);
  return response.json() as Promise<MeResponse>;
}

// Null when the update starts past the entries held: some were missed, and only the
// WholeHistory a reconnect brings can fill the gap.
function historyAfter(held: HistoryEntry[], update: HistoryUpdate): HistoryEntry[] | null {
  switch (update.kind) {
    case "WholeHistory": return update.values[0];
    case "NewEntries": {
      const [first, entries] = update.values;
      return first === held.length ? [...held, ...entries] : null;
    }
  }
}

// The engine names its seats; the board shows the display name of whoever sits in each.
function withDisplayNames(state: PublicState, pregame: PregamePlayer[]): PublicState {
  const players = Object.fromEntries(Object.entries(state.players).map(([pid, p]) => {
    const seated = pregame.find((x) => x.seat === pid);
    return [pid, seated ? { ...p, name: seated.display_name } : p];
  }));
  return { ...state, players };
}

const PAGE_TITLES: Record<TopNavTab, string> = {
  home: "On the Stack", decks: "Decks | On the Stack", profile: "Profile | On the Stack", settings: "Settings | On the Stack",
  admin: "Admin | On the Stack",
};

function pageRelease(): number | null {
  const n = /^\/r\/(\d+)\//.exec(location.pathname)?.[1];
  return n === undefined ? null : Number(n);
}
